/**
 * ControlPanel — TASK 9-2.
 *
 * Owner-admin-only CRM control panel. Provides:
 *   1. Leads DataTable with filter chips (Channel / Status / Followup window).
 *   2. Per-row right-side drawer: full lead details, outreach log timeline,
 *      "Add outreach" inline form, and direct "Edit status" control.
 *   3. "Add lead" modal (manual entry of all leads fields).
 *   4. Retargeting list: leads where status = 'contacted' AND
 *      last_contacted_at < now() - 7d.
 *   5. Churn watchlist: best-effort via admin_outreach_needed_users RPC
 *      (see [REVIEW] note — a dedicated admin_churn_watchlist() SECURITY
 *      DEFINER function should be created when production data is live).
 *   6. Vault subsection: encrypted API-key manager stub — [REVIEW].
 *
 * Hard rules:
 *   • Belt-and-suspenders isAdmin gate — every render path checks the JWT.
 *   • Never expose CRM data to non-admin: if !isAdmin the component renders
 *     a ForbiddenCard and returns. TopNav also hides the menu item.
 *   • notes / email / phone are RLS-protected at the DB layer; no extra
 *     client-side masking is required since the owner sees full data.
 */

import {
  ShieldAlert,
  RefreshCw,
  Users2,
  Plus,
  X,
  Send,
  Clock,
  Target,
  TrendingDown,
  Eye,
  EyeOff,
  ExternalLink,
  ChevronDown,
  AlertCircle,
  Info,
  MessageSquare,
  CheckCircle2,
  Trash2,
  Star,
  Search,
  Linkedin,
  Pencil,
  Mail,
  Phone,
  Briefcase,
  Upload,
  Download,
  Edit3,
  Save,
  KeyRound,
  Filter,
  ArrowUpDown,
  UserPlus,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { googleFaviconUrl } from '../../../utils/companyLogo';
import { DataTable } from '../../../components/DataTable';
import type { Column, FilterDef } from '../../../components/DataTable';
import { useAuthentication } from '../../../hooks/useAuthentication';
import { supabase } from '../../../lib/supabase';
import { loadExternalCreds } from './DeveloperSettings';
import { OWNER_EMAIL } from './Jobs/helpers';
import OutreachTabSection from './outreach/OutreachTabSection';
import RetargetingTabSection from './outreach/RetargetingTabSection';
import { sendOutreachIntroToUsers } from './outreach/outreachImportService';
import { formatRelativeSent } from './outreach/outreachEmailUtils';
import {
  CLOSED_SEND_MODAL,
  OutreachSendEmailModal,
  runOutreachSendWithModal,
  type OutreachSendModalState,
} from './outreach/OutreachSendEmailModal';
import Layout, { type DashboardPage } from './Layout';

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

type SourceChannel =
  | 'linkedin' | 'naukri' | 'facebook' | 'google' | 'github'
  | 'discord' | 'reddit' | 'direct' | 'referral' | 'other';

type LeadStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';

type FollowupWindow = 'overdue' | 'today' | 'this_week' | 'upcoming' | 'none';

interface Lead extends Record<string, unknown> {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  linkedin_url: string | null;
  linkedin_id: string | null;
  naukri_url: string | null;
  naukri_id: string | null;
  github_url: string | null;
  source_channel: SourceChannel;
  source_url: string | null;
  skills: string[];
  current_role: string | null;
  // enriched fields (leads_enriched migration)
  current_title: string | null;
  current_company: string | null;
  headline: string | null;
  profile_pic_url: string | null;
  experience_years: number | null;
  experience_min: number | null;
  experience_max: number | null;
  expected_salary_min: number | null;
  expected_salary_max: number | null;
  notice_period_days: number | null;
  last_active_at: string | null;
  qualification: string | null;
  resume_headline: string | null;
  is_open_to_work: boolean;
  engagement_score: number | null;
  location: string | null;
  status: LeadStatus;
  notes: string | null;
  last_contacted_at: string | null;
  next_followup_at: string | null;
  created_at: string;
  updated_at: string;
  // computed client-side for filter chip support
  followup_window: FollowupWindow;
}

interface OutreachLog extends Record<string, unknown> {
  id: string;
  lead_id: string;
  channel: string;
  message_snippet: string | null;
  sent_at: string;
  response: string | null;
  responded_at: string | null;
  created_at: string;
}

// Churn watchlist row from admin_outreach_needed_users RPC
interface ChurnRow extends Record<string, unknown> {
  user_id: string;
  name: string;
  email: string;
  created_at: string;
  days_idle: number;
  last_outreached_at: string | null;
}

// Peerlist job row — columns from 20260425220000_peerlist_jobs.sql
interface JobRow extends Record<string, unknown> {
  id: string;
  external_slug: string;
  url: string;
  title: string;
  company: string;
  company_logo_url: string | null;
  location: string | null;
  remote: boolean | null;
  job_type: string | null;
  experience_level: string | null;
  experience_min_years: number | null;
  experience_max_years: number | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  description_md: string | null;
  skills: string[] | null;
  posted_at: string | null;
  scraped_at: string;
  last_seen_at: string;
  is_active: boolean;
  // alias convenience
  source_channel?: string | null;
  source?: string | null;
  apply_url?: string | null;
}

type ControlTab = 'leads' | 'outreach' | 'retargeting' | 'churn' | 'jobs' | 'testimonials';

const CP_TAB_PARAM = 'cpTab';
const CP_TABS: ControlTab[] = ['leads', 'outreach', 'retargeting', 'churn', 'jobs'];

function readCpTabFromUrl(): ControlTab {
  if (typeof window === 'undefined') return 'leads';
  const raw = new URLSearchParams(window.location.search).get(CP_TAB_PARAM);
  if (raw && CP_TABS.includes(raw as ControlTab)) return raw as ControlTab;
  return 'leads';
}

function writeCpTabToUrl(next: ControlTab, mode: 'push' | 'replace' = 'push') {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (next === 'leads') {
    params.delete(CP_TAB_PARAM);
  } else {
    params.set(CP_TAB_PARAM, next);
  }
  const qs = params.toString();
  const newUrl = qs
    ? `${window.location.pathname}?${qs}${window.location.hash}`
    : `${window.location.pathname}${window.location.hash}`;
  if (mode === 'replace') window.history.replaceState({}, '', newUrl);
  else window.history.pushState({}, '', newUrl);
}

// ─────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────

const NEXT_ACTION_TO_PATH: Record<string, string> = {
  dashboard: '/dashboard',
  profile:   '/profile',
  credits:   '/credits',
  support:   '/support',
  jobs:      '/jobs',
};

const SOURCE_CHANNELS: { value: SourceChannel; label: string }[] = [
  { value: 'linkedin',  label: 'LinkedIn'  },
  { value: 'naukri',    label: 'Naukri'    },
  { value: 'facebook',  label: 'Facebook'  },
  { value: 'google',    label: 'Google'    },
  { value: 'github',    label: 'GitHub'    },
  { value: 'discord',   label: 'Discord'   },
  { value: 'reddit',    label: 'Reddit'    },
  { value: 'direct',    label: 'Direct'    },
  { value: 'referral',  label: 'Referral'  },
  { value: 'other',     label: 'Other'     },
];

const LEAD_STATUSES: { value: LeadStatus; label: string }[] = [
  { value: 'new',       label: 'New'       },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'converted', label: 'Converted' },
  { value: 'lost',      label: 'Lost'      },
];

// ─────────────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────────────

function computeFollowupWindow(iso: string | null): FollowupWindow {
  if (!iso) return 'none';
  const diffDays = (new Date(iso).getTime() - Date.now()) / 86_400_000;
  if (diffDays < 0)   return 'overdue';
  if (diffDays < 1)   return 'today';
  if (diffDays <= 7)  return 'this_week';
  return 'upcoming';
}

function enrichLead(raw: Record<string, unknown>): Lead {
  return {
    ...raw,
    followup_window: computeFollowupWindow(raw.next_followup_at as string | null),
  } as Lead;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// ─────────────────────────────────────────────────────────────────────────
// ControlPanel (root component)
// ─────────────────────────────────────────────────────────────────────────

export interface ControlPanelProps {
  onSignOut?: () => void | Promise<void>;
  /** Skip the Layout shell when rendered inside another page */
  embedded?: boolean;
  /** Parent page already verified admin — skip auth loading spinner on remount */
  parentIsAdmin?: boolean;
  /** When embedded, parent hides this panel — sync tab from URL when shown again */
  isVisible?: boolean;
}

const ControlPanel: React.FC<ControlPanelProps> = ({
  onSignOut = () => {},
  embedded = false,
  parentIsAdmin = false,
  isVisible = true,
}) => {
  const { session, user, loading: authLoading } = useAuthentication();

  const isAdmin = useMemo(() => {
    const email = (user?.email ?? session?.user?.email ?? '').toLowerCase();
    return email === OWNER_EMAIL.toLowerCase();
  }, [user?.email, session?.user?.email]);

  const handleNavigate = useCallback((page: DashboardPage) => {
    const target = NEXT_ACTION_TO_PATH[page] || '/dashboard';
    window.history.pushState({}, '', target);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  // ── State ──────────────────────────────────────────────────────────────
  const [activeTab,          setActiveTab]          = useState<ControlTab>(() => readCpTabFromUrl());

  const selectCpTab = useCallback((next: ControlTab) => {
    setActiveTab(next);
    writeCpTabToUrl(next);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => setActiveTab(readCpTabFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (isVisible) {
      setActiveTab(readCpTabFromUrl());
      return;
    }
    // Close portaled overlays when embedded panel is hidden (e.g. user switched
    // to another Account Settings tab) so fixed backdrops don't block inputs.
    setSelectedLead(null);
    setShowAddLead(false);
    setShowFindLeads(false);
    setShowImportCsv(false);
    setShowLeadDuplicates(false);
    document.body.style.overflow = '';
  }, [isVisible]);
  const [leads,              setLeads]              = useState<Lead[]>([]);
  const [loading,            setLoading]            = useState(false);
  const [error,              setError]              = useState<string | null>(null);
  const [selectedLead,       setSelectedLead]       = useState<Lead | null>(null);
  const [showAddLead,        setShowAddLead]         = useState(false);
  const [showFindLeads,      setShowFindLeads]       = useState(false);
  const [showImportCsv,      setShowImportCsv]      = useState(false);
  const [selectedIds,        setSelectedIds]        = useState<Set<string>>(new Set());
  const [deleting,           setDeleting]           = useState(false);
  // edit state for leads (single row inline edit via drawer)
  const [leadsEditMode,      setLeadsEditMode]      = useState(false);
  const leadsLoadedRef = useRef(false);
  const leadsRef = useRef(leads);
  leadsRef.current = leads;

  const sessionUserId = session?.user?.id ?? null;

  // ── Data loading ────────────────────────────────────────────────────────
  const loadLeads = useCallback(async (opts?: { silent?: boolean }) => {
    if (!sessionUserId || !isAdmin) {
      setLoading(false);
      return;
    }
    if (!opts?.silent && leadsRef.current.length === 0) setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setLeads((data ?? []).map(enrichLead));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load leads.');
    } finally {
      setLoading(false);
    }
  }, [sessionUserId, isAdmin]);

  useEffect(() => {
    if (!sessionUserId || !isAdmin) return;
    if (leadsLoadedRef.current) return;
    leadsLoadedRef.current = true;
    void loadLeads({ silent: true });
  }, [loadLeads, sessionUserId, isAdmin]);

  // ── Selection helpers ──────────────────────────────────────────────────
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAll    = useCallback(() => setSelectedIds(new Set(leads.map(l => l.id))), [leads]);
  const clearSel     = useCallback(() => setSelectedIds(new Set()), []);

  const deleteSelected = useCallback(async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Permanently delete ${selectedIds.size} lead(s)? This cannot be undone.`)) return;
    setDeleting(true);
    const ids = [...selectedIds];
    const { error: delErr } = await supabase.from('leads').delete().in('id', ids);
    setDeleting(false);
    if (delErr) { setError(delErr.message); return; }
    setSelectedIds(new Set());
    void loadLeads();
  }, [selectedIds, loadLeads]);

  // ── CSV export helper ─────────────────────────────────────────────────
  const exportCsv = useCallback((rows: Lead[], filename?: string) => {
    const headers = [
      'Name', 'Email', 'Phone', 'LinkedIn URL', 'GitHub URL',
      'Source', 'Status', 'Role', 'Location', 'Skills', 'Notes', 'Added',
    ];
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csvRows = rows.map(l => [
      l.full_name, l.email ?? '', l.phone ?? '', l.linkedin_url ?? '', l.github_url ?? '',
      l.source_channel, l.status, l.current_role ?? '', l.location ?? '',
      (l.skills ?? []).join('; '), l.notes ?? '', fmtDate(l.created_at),
    ].map(v => esc(String(v))).join(','));
    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = filename ?? `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // ── Find Duplicate leads ────────────────────────────────────────────────
  const [showLeadDuplicates, setShowLeadDuplicates] = useState(false);

  const leadDuplicateGroups = useMemo(() => {
    const groups = new Map<string, Lead[]>();
    for (const l of leads) {
      // key: normalised name OR same email OR same linkedin_url
      const byEmail    = l.email ? `email:${l.email.toLowerCase()}` : null;
      const byLinkedin = l.linkedin_url ? `li:${l.linkedin_url}` : null;
      const byName     = `name:${l.full_name.toLowerCase().trim()}`;
      const key = byEmail ?? byLinkedin ?? byName;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(l);
    }
    return [...groups.values()].filter(g => g.length > 1);
  }, [leads]);

  // ── Derived: retargeting (see RetargetingTabSection — outreach_persons) ──

  // ── DataTable columns (main leads table) — Exa Webset mirror ────────
  const leadCols = useMemo<Column<Lead>[]>(() => [
    { key: 'full_name', header: 'Name', minWidth: 260, alwaysVisible: true,
      cell: (_v, row) => {
        const initials = row.full_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
        const colors   = ['#6C47FF','#0EA5E9','#10B981','#F59E0B','#EF4444','#8B5CF6'];
        const bg       = colors[row.full_name.charCodeAt(0) % colors.length];
        return (
          <span className="flex items-center gap-2.5" onClick={e => e.stopPropagation()}>
            <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelect(row.id)}
              onClick={e => e.stopPropagation()}
              className="shrink-0 w-3.5 h-3.5 rounded accent-purple-600 cursor-pointer"
              aria-label={`Select ${row.full_name}`} />
            {row.profile_pic_url
              ? <img src={row.profile_pic_url} alt="" className="shrink-0 w-8 h-8 rounded-full object-cover ring-1 ring-black/5" />
              : <span className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white shadow-sm"
                  style={{ background: bg }}>{initials}</span>
            }
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5">
                <span className="ds-cp-text-strong truncate font-semibold text-[13px] cursor-pointer hover:underline"
                  onClick={e => { e.stopPropagation(); setSelectedLead(row); }}>
                  {row.full_name}
                </span>
                {row.is_open_to_work && (
                  <span className="ds-cp-meta-chip ds-cp-meta-chip--success shrink-0 text-[9px] whitespace-nowrap">Open to work</span>
                )}
                {!row.email && !row.phone && (
                  <span title="No contact info" className="ds-cp-meta-chip ds-cp-meta-chip--warn shrink-0 w-4 h-4 !p-0 flex items-center justify-center text-[9px] font-bold">!</span>
                )}
              </span>
              {(row.current_title || row.current_role) && (
                <span className="ds-cp-text-muted block truncate text-[11px] mt-0.5">
                  {row.current_title ?? row.current_role}
                </span>
              )}
            </span>
          </span>
        );
      },
    },
    { key: 'current_company', header: 'Company', minWidth: 160,
      cell: (v) => v
        ? <span className="ds-cp-text-strong text-[12px] font-medium truncate">{String(v)}</span>
        : <span className="ds-cp-text-muted">—</span>,
    },
    { key: 'current_title', header: 'Job Title', minWidth: 180,
      cell: (_v, row) => (
        <span className="ds-cp-text-body text-[12px] truncate">
          {row.current_title ?? row.current_role ?? <span className="ds-cp-text-muted italic">—</span>}
        </span>
      ),
    },
    { key: 'linkedin_url', header: 'URL', minWidth: 130, sortable: false,
      cell: (_v, row) => {
        const url = row.linkedin_url ?? row.naukri_url ?? row.source_url;
        if (!url) return <span className="ds-cp-text-muted">—</span>;
        const short = url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 20);
        return (
          <a href={url} target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="ds-cp-url-link truncate">
            {short}…
          </a>
        );
      },
    },
    { key: 'is_open_to_work', header: 'Currently See...', minWidth: 110,
      cell: (v) => (
        <span className={`ds-cp-meta-chip ${v ? 'ds-cp-meta-chip--success' : ''} text-[11px]`}>
          {v ? 'Match' : 'Match'}
        </span>
      ),
    },
    { key: 'location', header: 'Located In Lu...', minWidth: 150,
      cell: (v) => v ? (
        <span className="ds-cp-text-body flex items-center gap-1.5 text-[12px]">
          <span className="truncate">{String(v)}</span>
        </span>
      ) : <span className="ds-cp-text-muted">—</span>,
    },
    { key: 'email', header: 'Email', minWidth: 200,
      cell: (_v, row) => row.email
        ? <a href={`mailto:${row.email}`} className="ds-cp-email-link truncate"
            onClick={e => e.stopPropagation()}>{row.email}</a>
        : <span className="ds-cp-text-muted text-[11px] italic">Could not find data</span>,
    },
    { key: 'skills', header: 'Interests', minWidth: 200, sortable: false,
      cell: (v) => {
        const arr = (v as string[] | null) ?? [];
        if (!arr.length) return <span className="ds-cp-text-muted">Could not find data</span>;
        return (
          <span className="ds-cp-text-body text-[12px] truncate">
            {arr.join(', ')}
          </span>
        );
      },
    },
    { key: 'experience_years', header: 'Exp', minWidth: 70,
      cell: (_v, row) => {
        if (row.experience_min != null && row.experience_max != null)
          return <span className="text-[12px]">{row.experience_min}–{row.experience_max}y</span>;
        if (row.experience_years != null)
          return <span className="text-[12px]">{row.experience_years}y</span>;
        return <span className="ds-cp-text-muted">—</span>;
      },
    },
    { key: 'notice_period_days', header: 'Notice', minWidth: 70,
      cell: (v) => v == null
        ? <span className="ds-cp-text-muted">—</span>
        : <span className="ds-cp-text-body text-[12px]">{v === 0 ? 'Immediate' : `${v}d`}</span>,
    },
    { key: 'source_channel', header: 'Channel', minWidth: 100,
      cell: (v) => <ChannelBadge channel={String(v)} /> },
    { key: 'status', header: 'Status', minWidth: 100,
      cell: (v) => <LeadStatusBadge status={v as LeadStatus} /> },
    { key: 'engagement_score', header: 'Score', minWidth: 65,
      cell: (v) => v == null ? <span className="ds-cp-text-muted">—</span>
        : <EngagementScore value={Number(v)} />,
    },
    { key: 'created_at', header: 'Added', minWidth: 100,
      cell: (v) => <span className="ds-cp-text-body text-[12px]">{fmtDate(v as string)}</span> },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [selectedIds, toggleSelect]);

  const leadFilters = useMemo<FilterDef[]>(() => [
    {
      id: 'source_channel', label: 'Channel',
      options: SOURCE_CHANNELS,
    },
    {
      id: 'status', label: 'Status',
      options: LEAD_STATUSES,
    },
    {
      id: 'followup_window', label: 'Followup',
      options: [
        { value: 'overdue',   label: 'Overdue'    },
        { value: 'today',     label: 'Today'      },
        { value: 'this_week', label: 'This week'  },
        { value: 'upcoming',  label: 'Upcoming'   },
        { value: 'none',      label: 'Not set'    },
      ],
    },
  ], []);

  const Wrap = ({ children }: { children: React.ReactNode }) =>
    embedded ? <>{children}</> : (
      <Layout currentPage="profile" onNavigate={handleNavigate} onSignOut={onSignOut}>
        {children}
      </Layout>
    );

  // ── Auth gates ──────────────────────────────────────────────────────────
  const skipAuthSpinner = embedded && parentIsAdmin;

  if (authLoading && !skipAuthSpinner) return (
    <Wrap>
      <div className="flex items-center justify-center min-h-[40vh]">
        <LoadingSpinner size="lg" showText={false} />
      </div>
    </Wrap>
  );

  if ((!session || !isAdmin) && !skipAuthSpinner) return (
    <Wrap>
      <ForbiddenCard />
    </Wrap>
  );

  // ── Render ──────────────────────────────────────────────────────────────

  const TABS: { id: ControlTab; label: string; icon: React.ReactNode }[] = [
    { id: 'leads',        label: 'Leads',          icon: <Users2    className="w-3.5 h-3.5" /> },
    { id: 'outreach',     label: 'Outreach',       icon: <Mail      className="w-3.5 h-3.5" /> },
    { id: 'retargeting',  label: 'Retargeting',    icon: <Target    className="w-3.5 h-3.5" /> },
    { id: 'churn',        label: 'Onboarding',     icon: <TrendingDown className="w-3.5 h-3.5" /> },
    { id: 'jobs',         label: 'Jobs',           icon: <Briefcase className="w-3.5 h-3.5" /> },
    // Cron Jobs → standalone /admin/cron-jobs (Account → Cron Jobs)
  ];

  return (
    <Wrap>
      <div className="ds-control-panel px-2 sm:px-4 lg:px-6 py-4 sm:py-6 max-w-[1440px] mx-auto w-full min-w-0 space-y-6">

        {/* ── Page header — title only, actions live in each tab toolbar ── */}
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'var(--brand-purple-soft, rgba(108,71,255,0.10))', color: 'var(--brand-purple, #6C47FF)' }}
          >
            <Users2 className="w-5 h-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="ds-cp-page-title ggj-page-title text-2xl sm:text-3xl font-bold leading-tight">
              Control Panel
            </h1>
            <p className="ds-cp-page-desc text-[13px] sm:text-sm mt-0.5">
              Owner-only CRM — leads, outreach, retargeting &amp; churn.
            </p>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div
          className="ds-cp-tabs flex gap-1 border-b overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0 scrollbar-thin"
          style={{ WebkitOverflowScrolling: 'touch' }}
          role="tablist"
        >
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => selectCpTab(tab.id)}
              className={`ds-cp-tab inline-flex items-center gap-1.5 flex-shrink-0 min-h-[44px] px-3 py-2 text-xs sm:text-[13px] font-medium transition-colors relative whitespace-nowrap touch-manipulation ${
                activeTab === tab.id ? 'ds-cp-tab--active' : ''
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {error && (
          <div className="ds-cp-error rounded-xl px-3 py-2 text-[13px] flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" aria-hidden />
            {error}
          </div>
        )}

        {/* Tab panels stay mounted so switching tabs does not reset loading/data */}
        <div className="relative min-h-[420px]">
        {/* ── Tab: Leads (Exa Webset mirror) ── */}
        <div className={activeTab === 'leads' ? 'ds-cp-tab-panel flex flex-col gap-0' : 'hidden'} aria-hidden={activeTab !== 'leads'}>
            <div className="ds-cp-toolbar flex flex-wrap items-center gap-0">
              {/* Left cluster: select-all + filter / sort / count */}
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 cursor-pointer select-none pl-1">
                  <input type="checkbox"
                    checked={selectedIds.size === leads.length && leads.length > 0}
                    ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < leads.length; }}
                    onChange={() => selectedIds.size === leads.length ? clearSel() : selectAll()}
                    className="w-3.5 h-3.5 rounded accent-purple-600"
                  />
                </label>
                <span className="ds-cp-toolbar-count">{leads.length} results</span>
                <span className="ds-cp-toolbar-sep" aria-hidden />
                <button type="button" className="exa-toolbar-btn" onClick={() => void 0}><Filter className="w-3.5 h-3.5" /> Filter</button>
                <button type="button" className="exa-toolbar-btn" onClick={() => void 0}><ArrowUpDown className="w-3.5 h-3.5" /> Sort</button>
              </div>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Right cluster: actions */}
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setShowFindLeads(true)} className="exa-toolbar-btn exa-toolbar-btn-accent">
                  <UserPlus className="w-3.5 h-3.5" /> Find Leads
                </button>
                <button type="button" onClick={() => setShowAddLead(true)} className="exa-toolbar-btn">
                  <Plus className="w-3.5 h-3.5" /> Add
                </button>
                <span className="ds-cp-toolbar-sep" aria-hidden />
                <button type="button" onClick={() => setShowImportCsv(true)} className="exa-toolbar-btn" title="Import CSV">
                  <Upload className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => exportCsv(selectedIds.size > 0 ? leads.filter(l => selectedIds.has(l.id)) : leads)} disabled={leads.length === 0}
                  className="exa-toolbar-btn" title="Export CSV">
                  <Download className="w-3.5 h-3.5" />
                </button>
                {leadDuplicateGroups.length > 0 && (
                  <button type="button" onClick={() => setShowLeadDuplicates(true)}
                    className="exa-toolbar-btn exa-toolbar-btn--warn"
                    title={`${leadDuplicateGroups.reduce((s, g) => s + g.length - 1, 0)} duplicates`}>
                    <AlertCircle className="w-3.5 h-3.5" />
                  </button>
                )}
                {selectedIds.size > 0 && (
                  <>
                    <span className="ds-cp-toolbar-sep" aria-hidden />
                    <button type="button" disabled={selectedIds.size !== 1}
                      onClick={() => { const id = [...selectedIds][0]; const lead = leads.find(l => l.id === id); if (lead) setSelectedLead(lead); }}
                      className="exa-toolbar-btn" title="Edit selected">
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => void deleteSelected()} disabled={deleting}
                      className="exa-toolbar-btn exa-toolbar-btn--danger" title="Delete selected">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    {(() => {
                      const withEmail = leads.filter(l => selectedIds.has(l.id) && l.email);
                      return withEmail.length > 0 ? (
                        <a href={`mailto:${withEmail.map(l => l.email).join(',')}`}
                          className="exa-toolbar-btn exa-toolbar-btn--success" title="Email selected">
                          <Mail className="w-3.5 h-3.5" />
                        </a>
                      ) : null;
                    })()}
                    <span className="ds-cp-meta-chip ds-cp-meta-chip--brand">
                      {selectedIds.size} selected
                    </span>
                  </>
                )}
                <button type="button" onClick={() => void loadLeads({ silent: leads.length > 0 })} disabled={loading}
                  className="exa-toolbar-btn" title="Refresh">
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            <DataTable<Lead>
              columns={leadCols}
              rows={leads}
              filters={leadFilters}
              loading={false}
              onRowClick={setSelectedLead}
              onColumnsChange={() => undefined}
              pageSizeOptions={[20, 50, 100]}
              initialPageSize={20}
              reserveFloatingSpace
              ariaLabel="Leads table"
              emptyMessage="No leads yet — click 'Find Leads' or 'Add' to get started."
            />
        </div>

        {/* ── Tab: Personal Outreach (Excel/CSV import) ── */}
        <div className={activeTab === 'outreach' ? 'ds-cp-tab-panel flex flex-col gap-0' : 'hidden'} aria-hidden={activeTab !== 'outreach'}>
          <OutreachTabSection isActive={activeTab === 'outreach'} />
        </div>

        {/* ── Tab: Retargeting (outreach follow-ups 7d+) ── */}
        <div className={activeTab === 'retargeting' ? 'ds-cp-tab-panel flex flex-col gap-0' : 'hidden'} aria-hidden={activeTab !== 'retargeting'}>
          <RetargetingTabSection isActive={activeTab === 'retargeting'} />
        </div>

        {/* ── Tab: Onboarding (churn watchlist) ── */}
        <div className={activeTab === 'churn' ? 'flex flex-col gap-0' : 'hidden'} aria-hidden={activeTab !== 'churn'}>
          <ChurnWatchlistSection
            isAdmin={isAdmin}
            sessionUserId={sessionUserId}
            isActive={activeTab === 'churn'}
          />
        </div>

        {/* ── Tab: Jobs ── */}
        <div className={activeTab === 'jobs' ? 'flex flex-col gap-0' : 'hidden'} aria-hidden={activeTab !== 'jobs'}>
          <JobsTabSection
            isAdmin={isAdmin}
            sessionUserId={sessionUserId}
            isActive={activeTab === 'jobs'}
          />
        </div>

        {/* Testimonials moved to standalone page: /admin/testimonials */}
        {/* Cron Jobs moved to standalone page: /admin/cron-jobs */}

        </div>

      </div>

      {/* Lead detail drawer — only when panel is visible (portals escape hidden ancestors) */}
      {isVisible && selectedLead && (
        <LeadDrawer
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdated={() => { void loadLeads(); }}
        />
      )}

      {/* Add lead modal */}
      {isVisible && showAddLead && (
        <AddLeadModal
          onClose={() => setShowAddLead(false)}
          onSaved={() => { setShowAddLead(false); void loadLeads(); }}
        />
      )}

      {/* Find leads modal */}
      {isVisible && showFindLeads && (
        <FindLeadsModal
          onClose={() => setShowFindLeads(false)}
          onLeadsFound={() => { setShowFindLeads(false); void loadLeads(); }}
        />
      )}

      {/* Import CSV modal */}
      {isVisible && showImportCsv && (
        <ImportCsvModal
          onClose={() => setShowImportCsv(false)}
          onImported={() => { setShowImportCsv(false); void loadLeads(); }}
        />
      )}

      {/* Duplicate leads modal */}
      {isVisible && showLeadDuplicates && (
        <DuplicatesModal
          title="Duplicate Leads"
          groups={leadDuplicateGroups.map(g => g.map(l => ({
            id: l.id,
            label: l.full_name,
            sublabel: l.email ?? l.linkedin_url ?? l.source_channel,
            detail: `Added ${fmtDate(l.created_at)}`,
          })))}
          onClose={() => setShowLeadDuplicates(false)}
          onDelete={async (ids) => {
            const { error: delErr } = await supabase.from('leads').delete().in('id', ids);
            if (delErr) throw new Error(delErr.message);
            setShowLeadDuplicates(false);
            void loadLeads();
          }}
        />
      )}
    </Wrap>
  );
};

export default ControlPanel;

// ─────────────────────────────────────────────────────────────────────────
// DuplicatesModal — shared for Leads and Jobs
// ─────────────────────────────────────────────────────────────────────────

interface DupItem  { id: string; label: string; sublabel: string | null; detail: string; }
interface DuplicatesModalProps {
  title:    string;
  groups:   DupItem[][];
  onClose:  () => void;
  onDelete: (ids: string[]) => Promise<void>;
}

function DuplicatesModal({ title, groups, onClose, onDelete }: DuplicatesModalProps) {
  // Pre-select all but the first (newest) in each group
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(groups.flatMap(g => g.slice(1).map(i => i.id)))
  );
  const [deleting,   setDeleting]   = useState(false);
  const [deleteErr,  setDeleteErr]  = useState<string | null>(null);

  const toggle = (id: string) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const handleDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} duplicate record(s)? This cannot be undone.`)) return;
    setDeleting(true);
    setDeleteErr(null);
    try {
      await onDelete([...selected]);
    } catch (e) {
      setDeleteErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [onClose]);

  return createPortal(
    <>
      <div aria-hidden className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-[640px] max-h-[80vh] flex flex-col rounded-2xl overflow-hidden"
          style={{ background: 'var(--vscode-bg-primary)', border: '1px solid var(--vscode-border-primary)', boxShadow: '0 24px 64px rgba(0,0,0,0.32)' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b shrink-0"
            style={{ borderColor: 'var(--vscode-border-secondary)' }}>
            <div>
              <h2 className="text-[15px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>{title}</h2>
              <p className="text-[12px] mt-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>
                {groups.length} duplicate group{groups.length !== 1 ? 's' : ''} · {selected.size} selected for deletion
              </p>
            </div>
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70">
              <X className="w-4 h-4" style={{ color: 'var(--vscode-text-secondary)' }} />
            </button>
          </div>

          {/* Groups list */}
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
            {groups.map((group, gi) => (
              <div key={gi} className="rounded-xl border overflow-hidden"
                style={{ borderColor: 'rgba(245,158,11,0.25)', background: 'rgba(245,158,11,0.03)' }}>
                <div className="px-3 py-1.5 border-b text-[11px] font-semibold"
                  style={{ borderColor: 'rgba(245,158,11,0.2)', color: '#d97706', background: 'rgba(245,158,11,0.07)' }}>
                  {group[0].label} · {group.length} records
                </div>
                {group.map((item, idx) => (
                  <label key={item.id} className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors ${idx < group.length - 1 ? 'border-b' : ''}`}
                    style={{ borderColor: 'var(--vscode-border-secondary)', background: selected.has(item.id) ? 'rgba(239,68,68,0.04)' : 'transparent' }}>
                    <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggle(item.id)}
                      className="mt-0.5 shrink-0 w-3.5 h-3.5 accent-red-500" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[12px] font-medium truncate" style={{ color: 'var(--vscode-text-primary)' }}>
                          {item.label}
                        </span>
                        {idx === 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                            style={{ background: 'rgba(16,185,129,0.12)', color: '#059669' }}>KEEP</span>
                        )}
                        {selected.has(item.id) && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                            style={{ background: 'rgba(239,68,68,0.12)', color: '#dc2626' }}>DELETE</span>
                        )}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>
                        {item.sublabel && <span>{item.sublabel} · </span>}
                        <span>{item.detail}</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="flex flex-col gap-2 px-5 py-3 border-t shrink-0"
            style={{ borderColor: 'var(--vscode-border-secondary)' }}>
            {deleteErr && (
              <div className="px-3 py-2 rounded-lg text-[12px] flex items-start gap-2"
                style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c', border: '1px solid rgba(239,68,68,0.2)' }}>
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{deleteErr}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-[12px]" style={{ color: 'var(--vscode-text-secondary)' }}>
                Uncheck rows you want to keep. First row in each group is pre-marked KEEP.
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={onClose} className="min-h-[34px] px-3 rounded-lg text-[13px] border"
                  style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}>
                  Cancel
                </button>
                <button type="button" onClick={() => void handleDelete()}
                  disabled={selected.size === 0 || deleting}
                  className="min-h-[34px] px-4 rounded-lg text-[13px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: selected.size > 0 ? '#dc2626' : 'rgba(239,68,68,0.2)', color: 'white' }}>
                  <Trash2 className="w-3.5 h-3.5" />
                  {deleting ? 'Deleting…' : `Delete ${selected.size} duplicate${selected.size !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

// ─────────────────────────────────────────────────────────────────────────
// LeadDrawer — right-side detail panel
// ─────────────────────────────────────────────────────────────────────────

interface LeadDrawerProps {
  lead:      Lead;
  onClose:   () => void;
  onUpdated: () => void;
}

function LeadDrawer({ lead, onClose, onUpdated }: LeadDrawerProps) {
  const [log,            setLog]            = useState<OutreachLog[]>([]);
  const [logLoading,     setLogLoading]     = useState(true);
  const [showForm,       setShowForm]       = useState(false);
  const [statusEdit,     setStatusEdit]     = useState<LeadStatus>(lead.status);
  const [saving,         setSaving]         = useState(false);
  const [editContact,    setEditContact]    = useState(false);
  const [contactForm,    setContactForm]    = useState({ email: lead.email ?? '', phone: lead.phone ?? '' });
  const [contactSaving,  setContactSaving]  = useState(false);
  const [contactErr,     setContactErr]     = useState<string | null>(null);

  // Load outreach log for this lead
  useEffect(() => {
    let active = true;
    setLogLoading(true);
    supabase
      .from('lead_outreach_log')
      .select('*')
      .eq('lead_id', lead.id)
      .order('sent_at', { ascending: false })
      .then(({ data, error }) => {
        if (!active) return;
        if (!error) setLog((data ?? []) as OutreachLog[]);
        setLogLoading(false);
      });
    return () => { active = false; };
  }, [lead.id]);

  // Close on Escape
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleStatusSave = async () => {
    if (statusEdit === lead.status) return;
    setSaving(true);
    await supabase.from('leads').update({ status: statusEdit }).eq('id', lead.id);
    setSaving(false);
    onUpdated();
  };

  const handleContactSave = async () => {
    setContactErr(null);
    const email = contactForm.email.trim();
    const phone = contactForm.phone.trim();
    if (!email && !phone) {
      setContactErr('Email is required. Provide phone as fallback if email unavailable.');
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setContactErr('Enter a valid email address.');
      return;
    }
    setContactSaving(true);
    const { error } = await supabase
      .from('leads')
      .update({ email: email || null, phone: phone || null })
      .eq('id', lead.id);
    setContactSaving(false);
    if (error) { setContactErr(error.message); return; }
    setEditContact(false);
    onUpdated();
  };

  const handleOutreachSaved = () => {
    setShowForm(false);
    // Reload log
    supabase
      .from('lead_outreach_log')
      .select('*')
      .eq('lead_id', lead.id)
      .order('sent_at', { ascending: false })
      .then(({ data }) => setLog((data ?? []) as OutreachLog[]));
    onUpdated();
  };

  return createPortal(
    <>
      <div
        aria-hidden="true"
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Lead: ${lead.full_name}`}
        className="fixed right-0 top-0 bottom-0 z-50 flex flex-col w-[400px] sm:w-[480px] shadow-2xl overflow-hidden"
        style={{
          background: 'var(--surface-card-elevated, var(--vscode-bg-elevated))',
          borderLeft: '1px solid var(--vscode-border-primary)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b shrink-0"
          style={{ borderColor: 'var(--vscode-border-secondary)' }}
        >
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold truncate" style={{ color: 'var(--vscode-text-primary)' }}>
              {lead.full_name}
            </h2>
            <p className="text-[12px] mt-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>
              {lead.source_channel} · added {fmtDate(lead.created_at)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors hover:bg-[color:var(--vscode-bg-hover,rgba(0,0,0,0.06))] shrink-0 ml-2"
            aria-label="Close"
          >
            <X className="w-4 h-4" style={{ color: 'var(--vscode-text-secondary)' }} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* ── Exa-style hero card ── */}
          <div className="flex items-start gap-3">
            {lead.profile_pic_url
              ? <img src={lead.profile_pic_url} alt="" className="shrink-0 w-12 h-12 rounded-full object-cover ring-2 ring-[var(--brand-purple,#6C47FF)]/20" />
              : (() => {
                  const initials = lead.full_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
                  const colors = ['#6C47FF','#0EA5E9','#10B981','#F59E0B','#EF4444','#8B5CF6'];
                  const bg = colors[lead.full_name.charCodeAt(0) % colors.length];
                  return (
                    <span className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-[14px] font-bold text-white ring-2 ring-white/20"
                      style={{ background: bg }}>{initials}</span>
                  );
                })()
            }
            <div className="flex-1 min-w-0">
              <h3 className="text-[16px] font-bold truncate" style={{ color: 'var(--vscode-text-primary)' }}>
                {lead.full_name}
              </h3>
              <p className="text-[12px] truncate" style={{ color: 'var(--vscode-text-secondary)' }}>
                {lead.current_title ?? lead.current_role ?? 'No role'}
                {lead.current_company && <span> @ {lead.current_company}</span>}
              </p>
              <p className="text-[11px] mt-0.5" style={{ color: 'var(--vscode-text-tertiary)' }}>
                {lead.location ?? 'Location unknown'}
              </p>
            </div>
          </div>

          {/* ── Relevance Summary (Exa-style) ── */}
          {(lead.headline || lead.resume_headline) && (
            <div className="rounded-xl p-3.5"
              style={{ background: 'rgba(108,71,255,0.04)', border: '1px solid rgba(108,71,255,0.15)' }}>
              <h4 className="text-[11px] font-bold uppercase tracking-wider mb-2"
                style={{ color: 'var(--brand-purple, #6C47FF)' }}>
                Relevance Summary
              </h4>
              <p className="text-[12.5px] leading-relaxed" style={{ color: 'var(--vscode-text-secondary)' }}>
                {lead.headline || lead.resume_headline}
              </p>
            </div>
          )}

          {/* ── Criteria Evaluations (Exa-style checkmarks) ── */}
          <div className="rounded-xl p-3.5"
            style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)' }}>
            <h4 className="text-[11px] font-bold uppercase tracking-wider mb-3"
              style={{ color: 'var(--vscode-text-tertiary)' }}>
              Criteria Evaluations
            </h4>
            <div className="space-y-2.5">
              {/* Open to work */}
              <div className="flex items-start gap-2.5">
                <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${lead.is_open_to_work ? 'text-white' : ''}`}
                  style={{ background: lead.is_open_to_work ? '#10B981' : 'rgba(107,114,128,0.15)', color: lead.is_open_to_work ? '#fff' : '#9ca3af' }}>
                  {lead.is_open_to_work ? '✓' : '—'}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="text-[12px] font-semibold" style={{ color: lead.is_open_to_work ? '#059669' : 'var(--vscode-text-tertiary)' }}>
                    {lead.is_open_to_work ? 'actively seeking opportunities' : 'not marked as seeking'}
                  </span>
                </div>
              </div>
              {/* Has contact info */}
              <div className="flex items-start gap-2.5">
                <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${lead.email ? 'text-white' : ''}`}
                  style={{ background: lead.email ? '#10B981' : 'rgba(234,88,12,0.15)', color: lead.email ? '#fff' : '#ea580c' }}>
                  {lead.email ? '✓' : '!'}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="text-[12px] font-semibold" style={{ color: lead.email ? '#059669' : '#ea580c' }}>
                    {lead.email ? 'has email contact' : 'no email — needs enrichment'}
                  </span>
                </div>
              </div>
              {/* Has LinkedIn */}
              <div className="flex items-start gap-2.5">
                <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${lead.linkedin_url ? 'text-white' : ''}`}
                  style={{ background: lead.linkedin_url ? '#0A66C2' : 'rgba(107,114,128,0.15)', color: lead.linkedin_url ? '#fff' : '#9ca3af' }}>
                  {lead.linkedin_url ? '✓' : '—'}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="text-[12px] font-semibold" style={{ color: lead.linkedin_url ? '#0a66c2' : 'var(--vscode-text-tertiary)' }}>
                    {lead.linkedin_url ? 'LinkedIn profile linked' : 'no LinkedIn profile'}
                  </span>
                </div>
              </div>
              {/* Location */}
              {lead.location && (
                <div className="flex items-start gap-2.5">
                  <span className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                    style={{ background: '#10B981' }}>✓</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-[12px] font-semibold" style={{ color: '#059669' }}>
                      located in {lead.location}
                    </span>
                  </div>
                </div>
              )}
              {/* Skills */}
              {lead.skills?.length > 0 && (
                <div className="flex items-start gap-2.5">
                  <span className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                    style={{ background: '#6C47FF' }}>✓</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-[12px] font-semibold" style={{ color: 'var(--brand-purple, #6C47FF)' }}>
                      {lead.skills.length} skill{lead.skills.length !== 1 ? 's' : ''} identified
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Enrichments (Exa-style pill tags) ── */}
          <div>
            <h4 className="text-[11px] font-bold uppercase tracking-wider mb-2"
              style={{ color: 'var(--vscode-text-tertiary)' }}>
              Enrichments
              <span className="ml-1.5 text-[10px] font-normal px-1.5 py-0.5 rounded-full"
                style={{ background: 'rgba(108,71,255,0.10)', color: 'var(--brand-purple, #6C47FF)' }}>
                {[lead.email, lead.phone, lead.linkedin_url, lead.github_url, lead.skills?.length, lead.location].filter(Boolean).length} / row
              </span>
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {lead.email && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium"
                  style={{ background: 'rgba(16,185,129,0.10)', color: '#059669', border: '1px solid rgba(16,185,129,0.25)' }}>
                  <Mail className="w-3 h-3" /> Email
                </span>
              )}
              {lead.phone && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium"
                  style={{ background: 'rgba(16,185,129,0.10)', color: '#059669', border: '1px solid rgba(16,185,129,0.25)' }}>
                  <Phone className="w-3 h-3" /> Phone
                </span>
              )}
              {lead.linkedin_url && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium"
                  style={{ background: 'rgba(10,102,194,0.10)', color: '#0a66c2', border: '1px solid rgba(10,102,194,0.25)' }}>
                  <Linkedin className="w-3 h-3" /> LinkedIn
                </span>
              )}
              {lead.skills?.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium"
                  style={{ background: 'rgba(108,71,255,0.08)', color: 'var(--brand-purple,#6C47FF)', border: '1px solid rgba(108,71,255,0.20)' }}>
                  Skills ({lead.skills.length})
                </span>
              )}
              {lead.experience_years != null && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium"
                  style={{ background: 'rgba(14,165,233,0.08)', color: '#0284c7', border: '1px solid rgba(14,165,233,0.20)' }}>
                  {lead.experience_years}y exp
                </span>
              )}
              {lead.notice_period_days != null && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium"
                  style={{ background: 'rgba(245,158,11,0.08)', color: '#b45309', border: '1px solid rgba(245,158,11,0.20)' }}>
                  {lead.notice_period_days === 0 ? 'Immediate' : `${lead.notice_period_days}d notice`}
                </span>
              )}
            </div>
          </div>

          {/* ── Pipeline status ── */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5"
                   style={{ color: 'var(--vscode-text-tertiary)' }}>
              Pipeline status
            </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <select
                  value={statusEdit}
                  onChange={(e) => setStatusEdit(e.target.value as LeadStatus)}
                  className="w-full appearance-none rounded-lg px-3 py-2 text-[13px] pr-8 focus:outline-none"
                  style={{
                    background:   'var(--vscode-bg-secondary)',
                    border:       '1px solid var(--vscode-border-primary)',
                    color:        'var(--vscode-text-primary)',
                  }}
                >
                  {LEAD_STATUSES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
                             style={{ color: 'var(--vscode-text-tertiary)' }} />
              </div>
              <button
                type="button"
                onClick={() => void handleStatusSave()}
                disabled={saving || statusEdit === lead.status}
                className="px-3 py-2 rounded-lg text-[13px] font-medium transition-colors disabled:opacity-40"
                style={{
                  background: 'var(--brand-purple, #6C47FF)',
                  color: '#fff',
                }}
              >
                {saving ? '…' : 'Save'}
              </button>
            </div>
          </div>

          {/* Contact details */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] font-bold uppercase tracking-wider"
                  style={{ color: 'var(--vscode-text-tertiary)' }}>
                Contact
              </h3>
              <button
                type="button"
                onClick={() => { setEditContact(v => !v); setContactErr(null); setContactForm({ email: lead.email ?? '', phone: lead.phone ?? '' }); }}
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded transition-colors"
                style={{ color: 'var(--brand-purple, #6C47FF)', background: 'transparent' }}
              >
                <Pencil className="w-3 h-3" />
                {editContact ? 'Cancel' : (!lead.email && !lead.phone ? 'Add contact info' : 'Edit')}
              </button>
            </div>

            {/* No-contact warning banner */}
            {!lead.email && !lead.phone && !editContact && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg mb-3"
                   style={{ background: 'rgba(234,88,12,0.08)', border: '1px solid rgba(234,88,12,0.2)' }}>
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: '#ea580c' }} />
                <p className="text-[12px]" style={{ color: '#c2410c' }}>
                  No contact info. Click <strong>Add contact info</strong> to save an email or phone so you can reach this lead.
                </p>
              </div>
            )}

            {/* Inline contact editor */}
            {editContact ? (
              <div className="space-y-2.5 p-3 rounded-lg"
                   style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)' }}>
                {contactErr && (
                  <p className="text-[11.5px] px-2 py-1.5 rounded"
                     style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c' }}>
                    {contactErr}
                  </p>
                )}
                <div className="space-y-1">
                  <label className="flex items-center gap-1 text-[11px] font-medium" style={{ color: 'var(--vscode-text-secondary)' }}>
                    <Mail className="w-3 h-3" /> Email <span style={{ color: '#ea580c' }}>*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="jane@example.com"
                    value={contactForm.email}
                    onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))}
                    autoFocus
                    style={inputStyle}
                    className="form-input-sm w-full"
                  />
                </div>
                <div className="space-y-1">
                  <label className="flex items-center gap-1 text-[11px] font-medium" style={{ color: 'var(--vscode-text-secondary)' }}>
                    <Phone className="w-3 h-3" /> Phone <span style={{ color: 'var(--vscode-text-tertiary)', fontSize: 10 }}>(fallback)</span>
                  </label>
                  <input
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={contactForm.phone}
                    onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))}
                    style={inputStyle}
                    className="form-input-sm w-full"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void handleContactSave()}
                  disabled={contactSaving}
                  className="w-full py-1.5 rounded-lg text-[13px] font-medium transition-colors disabled:opacity-50"
                  style={{ background: 'var(--brand-purple, #6C47FF)', color: '#fff' }}
                >
                  {contactSaving ? 'Saving…' : 'Save contact info'}
                </button>
              </div>
            ) : (
              <div className="space-y-1.5">
                <DetailRow label="Email"    value={lead.email} link={lead.email ? `mailto:${lead.email}` : null} />
                <DetailRow label="Phone"    value={lead.phone} />
                <DetailRow label="Location" value={lead.location} />
                {lead.linkedin_url && (
                  <DetailRow label="LinkedIn" value="Open profile"
                    link={lead.linkedin_url} icon={<ExternalLink className="w-3 h-3" />} />
                )}
                {lead.github_url && (
                  <DetailRow label="GitHub" value="Open profile"
                    link={lead.github_url} icon={<ExternalLink className="w-3 h-3" />} />
                )}
              </div>
            )}
          </div>

          {/* Professional */}
          <DetailSection title="Professional">
            <DetailRow label="Channel"  value={lead.source_channel} />
            <DetailRow label="Skills"   value={lead.skills?.length ? lead.skills.join(', ') : null} />
            <DetailRow label="Source URL" value={lead.source_url} link={lead.source_url} />
          </DetailSection>

          {/* Followup */}
          <DetailSection title="Followup">
            <DetailRow label="Last contacted"  value={fmtDateTime(lead.last_contacted_at)} />
            <DetailRow label="Next followup"   value={fmtDateTime(lead.next_followup_at)} />
          </DetailSection>

          {/* Notes — sensitive, RLS-protected at DB layer */}
          {lead.notes && (
            <DetailSection title="Notes">
              <p className="text-[13px] whitespace-pre-wrap" style={{ color: 'var(--vscode-text-secondary)' }}>
                {lead.notes}
              </p>
            </DetailSection>
          )}

          {/* Outreach log */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] font-bold uppercase tracking-wider"
                  style={{ color: 'var(--vscode-text-tertiary)' }}>
                Outreach log ({log.length})
              </h3>
              <button
                type="button"
                onClick={() => setShowForm(v => !v)}
                className="inline-flex items-center gap-1 text-[12px] font-medium px-2 py-1 rounded-lg transition-colors"
                style={{
                  background: showForm ? 'var(--vscode-bg-tertiary)' : 'var(--brand-purple, #6C47FF)',
                  color: showForm ? 'var(--vscode-text-secondary)' : '#fff',
                }}
              >
                {showForm ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                {showForm ? 'Cancel' : 'Add outreach'}
              </button>
            </div>

            {/* Add outreach inline form */}
            {showForm && (
              <AddOutreachForm
                leadId={lead.id}
                onSaved={handleOutreachSaved}
              />
            )}

            {/* Timeline */}
            {logLoading ? (
              <div className="flex items-center gap-2 py-3 text-[13px]"
                   style={{ color: 'var(--vscode-text-tertiary)' }}>
                <Clock className="w-3.5 h-3.5 animate-pulse" aria-hidden />
                Loading outreach history…
              </div>
            ) : log.length === 0 ? (
              <p className="text-[13px] py-2" style={{ color: 'var(--vscode-text-tertiary)' }}>
                No outreach recorded yet.
              </p>
            ) : (
              <ol className="space-y-2">
                {log.map(entry => (
                  <li
                    key={entry.id}
                    className="rounded-lg px-3 py-2.5 text-[12.5px] space-y-1"
                    style={{
                      background: 'var(--vscode-bg-secondary)',
                      border: '1px solid var(--vscode-border-secondary)',
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold capitalize" style={{ color: 'var(--vscode-text-primary)' }}>
                        {entry.channel}
                      </span>
                      <span style={{ color: 'var(--vscode-text-tertiary)' }}>
                        {fmtDate(entry.sent_at)}
                      </span>
                    </div>
                    {entry.message_snippet && (
                      <p className="line-clamp-2" style={{ color: 'var(--vscode-text-secondary)' }}>
                        {entry.message_snippet}
                      </p>
                    )}
                    {entry.response && (
                      <p className="italic" style={{ color: 'var(--vscode-text-tertiary)' }}>
                        Reply: {entry.response}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </aside>
    </>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────
// AddOutreachForm — inline form within the drawer
// ─────────────────────────────────────────────────────────────────────────

interface AddOutreachFormProps {
  leadId:   string;
  onSaved:  () => void;
}

function AddOutreachForm({ leadId, onSaved }: AddOutreachFormProps) {
  const [channel,  setChannel]  = useState('linkedin');
  const [snippet,  setSnippet]  = useState('');
  const [response, setResponse] = useState('');
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!channel.trim()) { setErr('Channel is required.'); return; }
    setSaving(true);
    setErr(null);
    const { error } = await supabase.from('lead_outreach_log').insert({
      lead_id:         leadId,
      channel:         channel.trim(),
      message_snippet: snippet.trim() || null,
      response:        response.trim() || null,
      sent_at:         new Date().toISOString(),
    });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    // Update last_contacted_at on the lead
    await supabase.from('leads').update({ last_contacted_at: new Date().toISOString() }).eq('id', leadId);
    onSaved();
  };

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="rounded-xl p-3 mb-3 space-y-2.5"
      style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)' }}
    >
      {err && (
        <p className="text-[12px]" style={{ color: '#b91c1c' }}>{err}</p>
      )}
      <FieldRow label="Channel *">
        <select
          value={channel}
          onChange={e => setChannel(e.target.value)}
          className="form-select-sm"
          style={selectStyle}
        >
          {['linkedin','email','whatsapp','discord','twitter','call','other'].map(c => (
            <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
          ))}
        </select>
      </FieldRow>
      <FieldRow label="Message snippet">
        <textarea
          rows={2}
          placeholder="Paste the first line of your message…"
          value={snippet}
          onChange={e => setSnippet(e.target.value)}
          className="w-full rounded-lg px-2.5 py-1.5 text-[12.5px] resize-none focus:outline-none"
          style={inputStyle}
        />
      </FieldRow>
      <FieldRow label="Their response">
        <input
          type="text"
          placeholder="Short summary (optional)"
          value={response}
          onChange={e => setResponse(e.target.value)}
          className="w-full rounded-lg px-2.5 py-1.5 text-[12.5px] focus:outline-none"
          style={inputStyle}
        />
      </FieldRow>
      <button
        type="submit"
        disabled={saving}
        className="w-full py-2 rounded-lg text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
        style={{ background: 'var(--brand-purple, #6C47FF)', color: '#fff' }}
      >
        <Send className="w-3.5 h-3.5" />
        {saving ? 'Saving…' : 'Log outreach'}
      </button>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// AddLeadModal — centered dialog for creating a new lead
// ─────────────────────────────────────────────────────────────────────────

interface AddLeadModalProps {
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY_FORM = {
  full_name:      '',
  email:          '',
  phone:          '',
  linkedin_url:   '',
  github_url:     '',
  source_channel: 'linkedin' as SourceChannel,
  source_url:     '',
  skills:         '',
  current_role:   '',
  location:       '',
  status:         'new' as LeadStatus,
  notes:          '',
  next_followup_at: '',
};

function AddLeadModal({ onClose, onSaved }: AddLeadModalProps) {
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', h);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const set = (field: keyof typeof EMPTY_FORM) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim())      { setErr('Full name is required.'); return; }
    if (!form.source_channel.trim()) { setErr('Source channel is required.'); return; }
    if (!form.email.trim() && !form.phone.trim()) {
      setErr('Email is required. If unavailable, provide a phone number as fallback.');
      return;
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setErr('Enter a valid email address.');
      return;
    }

    setSaving(true);
    setErr(null);
    const { error } = await supabase.from('leads').insert({
      full_name:        form.full_name.trim(),
      email:            form.email.trim()        || null,
      phone:            form.phone.trim()        || null,
      linkedin_url:     form.linkedin_url.trim() || null,
      github_url:       form.github_url.trim()   || null,
      source_channel:   form.source_channel,
      source_url:       form.source_url.trim()   || null,
      skills:           form.skills.trim()
                          ? form.skills.split(',').map(s => s.trim()).filter(Boolean)
                          : [],
      current_role:     form.current_role.trim() || null,
      location:         form.location.trim()     || null,
      status:           form.status,
      notes:            form.notes.trim()        || null,
      next_followup_at: form.next_followup_at    || null,
    });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  };

  return createPortal(
    <>
      <div
        aria-hidden="true"
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add lead"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div
          className="w-full max-w-[560px] max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl flex flex-col"
          style={{
            background: 'var(--surface-card-elevated, var(--vscode-bg-elevated))',
            border:     '1px solid var(--vscode-border-primary)',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Modal header */}
          <div
            className="flex items-center justify-between px-5 py-4 border-b shrink-0"
            style={{ borderColor: 'var(--vscode-border-secondary)' }}
          >
            <h2 className="text-[15px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
              Add new lead
            </h2>
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-[color:var(--vscode-bg-hover)]">
              <X className="w-4 h-4" style={{ color: 'var(--vscode-text-secondary)' }} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={(e) => void handleSubmit(e)} className="p-5 space-y-3">
            {err && (
              <p className="text-[12.5px] px-3 py-2 rounded-lg"
                 style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c' }}>
                {err}
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ModalField label="Full name *">
                <input type="text" required placeholder="Jane Doe" value={form.full_name} onChange={set('full_name')} style={inputStyle} className="form-input-sm" />
              </ModalField>
              <ModalField label="Email *" hint="Required — or provide phone">
                <input type="email" placeholder="jane@example.com" value={form.email} onChange={set('email')} style={inputStyle} className="form-input-sm" autoComplete="email" />
              </ModalField>
              <ModalField label="Phone" hint="Fallback if no email">
                <input type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={set('phone')} style={inputStyle} className="form-input-sm" />
              </ModalField>
              <ModalField label="Current role">
                <input type="text" placeholder="Senior Engineer" value={form.current_role} onChange={set('current_role')} style={inputStyle} className="form-input-sm" />
              </ModalField>
              <ModalField label="Source channel *">
                <select required value={form.source_channel} onChange={set('source_channel')} style={selectStyle} className="form-select-sm">
                  {SOURCE_CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </ModalField>
              <ModalField label="Status">
                <select value={form.status} onChange={set('status')} style={selectStyle} className="form-select-sm">
                  {LEAD_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </ModalField>
              <ModalField label="Location">
                <input type="text" placeholder="Bengaluru, India" value={form.location} onChange={set('location')} style={inputStyle} className="form-input-sm" />
              </ModalField>
              <ModalField label="Next followup">
                <input type="datetime-local" value={form.next_followup_at} onChange={set('next_followup_at')} style={inputStyle} className="form-input-sm" />
              </ModalField>
              <ModalField label="LinkedIn URL">
                <input type="url" placeholder="https://linkedin.com/in/…" value={form.linkedin_url} onChange={set('linkedin_url')} style={inputStyle} className="form-input-sm" />
              </ModalField>
              <ModalField label="GitHub URL">
                <input type="url" placeholder="https://github.com/…" value={form.github_url} onChange={set('github_url')} style={inputStyle} className="form-input-sm" />
              </ModalField>
            </div>

            <ModalField label="Skills (comma-separated)">
              <input type="text" placeholder="React, TypeScript, Node.js" value={form.skills} onChange={set('skills')} style={inputStyle} className="form-input-sm" />
            </ModalField>

            <ModalField label="Notes">
              <textarea
                rows={3}
                placeholder="Anything sensitive about this lead…"
                value={form.notes}
                onChange={set('notes')}
                className="w-full rounded-lg px-3 py-2 text-[13px] resize-none focus:outline-none"
                style={inputStyle}
              />
            </ModalField>

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl text-[13px] font-medium transition-colors"
                style={{ border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold transition-colors disabled:opacity-50"
                style={{ background: 'var(--brand-purple, #6C47FF)', color: '#fff' }}
              >
                {saving ? 'Saving…' : 'Add lead'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────
// FindLeadsModal — auto-discover leads from LinkedIn / Naukri
// ─────────────────────────────────────────────────────────────────────────

interface FindLeadsModalProps {
  onClose:       () => void;
  onLeadsFound:  () => void;
}

function FindLeadsModal({ onClose, onLeadsFound }: FindLeadsModalProps) {
  const [keywords,   setKeywords]   = useState('');
  const [location,   setLocation]   = useState('India');
  const [channel,    setChannel]    = useState<'both' | 'linkedin' | 'naukri'>('linkedin');
  const [maxRes,     setMaxRes]     = useState(20);
  const [openToWork, setOpenToWork] = useState(false);
  const [expMin,     setExpMin]     = useState('');
  const [expMax,     setExpMax]     = useState('');
  const [running,    setRunning]    = useState(false);
  const [result,     setResult]     = useState<{ inserted: number; found: number; skipped: number; diagnostics?: Record<string, string> } | null>(null);
  const [err,        setErr]        = useState<string | null>(null);

  const extCreds = loadExternalCreds();
  const credMap: Record<string, string> = {};
  for (const c of extCreds) credMap[c.key] = c.value;

  const hasLinkedIn = !!credMap['linkedin_li_at'];
  const hasNaukri   = !!credMap['naukri_nauk_at'];

  const handleFind = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keywords.trim()) { setErr('Enter keywords (role, skills, or name).'); return; }
    setRunning(true); setErr(null); setResult(null);

    const liAt = typeof credMap['linkedin_li_at'] === 'string' ? credMap['linkedin_li_at'] : undefined;
    const nkAt = typeof credMap['naukri_nauk_at'] === 'string' ? credMap['naukri_nauk_at'] : undefined;

    const rawBody = {
      channel,
      max_results: maxRes,
      credentials: {
        ...(liAt ? { linkedin_li_at: liAt } : {}),
        ...(nkAt ? { naukri_nauk_at: nkAt } : {}),
      },
      linkedin: {
        keywords:     keywords.trim(),
        location:     location.trim() || 'India',
        ...(openToWork ? { open_to_work: true as const } : {}),
      },
      naukri: {
        keywords:       keywords.trim(),
        ...(location.trim() ? { location: [location.trim()] as string[] } : {}),
        ...(expMin ? { experience_min: Number(expMin) } : {}),
        ...(expMax ? { experience_max: Number(expMax) } : {}),
      },
    };

    let body: Record<string, unknown>;
    try {
      body = JSON.parse(JSON.stringify(rawBody)) as Record<string, unknown>;
    } catch {
      setRunning(false);
      setErr('Could not serialize request (check saved credentials for invalid characters).');
      return;
    }

    const { data, error: fnErr } = await supabase.functions.invoke<{
      ok: boolean; inserted: number; found: number; skipped: number;
      diagnostics?: Record<string, string>; error?: string; hint?: string;
    }>('find-leads', { body });

    setRunning(false);
    if (fnErr || !data?.ok) {
      const hint = data?.hint ? ` (${data.hint})` : '';
      setErr((fnErr?.message ?? data?.error ?? 'Agent failed') + hint);
      return;
    }
    setResult({ inserted: data.inserted, found: data.found, skipped: data.skipped, diagnostics: data.diagnostics });
    setTimeout(() => onLeadsFound(), data.inserted > 0 ? 1500 : 3000);
  };

  const modalStyle: React.CSSProperties = {
    background: 'var(--vscode-bg-primary)',
    border: '1px solid var(--vscode-border-primary)',
    borderRadius: 16,
    width: '100%',
    maxWidth: 520,
    padding: 28,
    boxShadow: '0 24px 64px rgba(0,0,0,0.32)',
  };

  return createPortal(
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9998 }}
        onClick={onClose}
        aria-hidden
      />
      <div
        style={{
          position: 'fixed', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: 16,
        }}
      >
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-[16px] font-bold" style={{ color: 'var(--vscode-text-primary)' }}>
                Find leads agent
              </h2>
              <p className="text-[12px] mt-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>
                Searches LinkedIn &amp; Naukri for matching candidates and adds them to your leads.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:opacity-70 transition-opacity"
              style={{ background: 'var(--vscode-bg-secondary)', color: 'var(--vscode-text-secondary)' }}
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Channel status */}
          <div className="rounded-xl p-3 mb-4 space-y-2 text-[12px]"
               style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)' }}>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span style={{ color: hasLinkedIn ? '#10b981' : '#6366f1' }}>
                {hasLinkedIn ? '●' : '○'} LinkedIn {hasLinkedIn ? '(authenticated — full search)' : '(public search — limited)'}
              </span>
              <span style={{ color: '#f59e0b' }}>
                ◐ Naukri — requires <strong>Naukri Resdex</strong> paid subscription
              </span>
            </div>
            <p style={{ color: 'var(--vscode-text-tertiary)' }}>
              Naukri candidate database (Resdex) is a paid recruiter service. Without a Resdex session, Naukri returns job listings, not people.
              Use LinkedIn for people search, or Import CSV to add Naukri candidates manually.
            </p>
          </div>

          <form onSubmit={(e) => void handleFind(e)} className="space-y-4">
            {/* Keywords */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                  Keywords / Role *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Product Manager, React Developer"
                  value={keywords}
                  onChange={e => setKeywords(e.target.value)}
                  autoFocus
                  className="w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none"
                  style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>Location</label>
                <input
                  type="text"
                  placeholder="Bengaluru, India"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none"
                  style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>Experience (years)</label>
                <div className="flex gap-2">
                  <input type="number" min={0} max={30} placeholder="Min" value={expMin}
                    onChange={e => setExpMin(e.target.value)}
                    className="w-full rounded-lg px-2 py-2 text-[13px] focus:outline-none"
                    style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }} />
                  <input type="number" min={0} max={30} placeholder="Max" value={expMax}
                    onChange={e => setExpMax(e.target.value)}
                    className="w-full rounded-lg px-2 py-2 text-[13px] focus:outline-none"
                    style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }} />
                </div>
              </div>
            </div>

            {/* Open to work toggle */}
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={openToWork} onChange={e => setOpenToWork(e.target.checked)}
                className="w-3.5 h-3.5 accent-purple-600" />
              <span className="text-[12px]" style={{ color: 'var(--vscode-text-secondary)' }}>LinkedIn: Open to Work only</span>
            </label>

            {/* Channel */}
            <div>
              <label className="block text-[12px] font-medium mb-1.5" style={{ color: 'var(--vscode-text-secondary)' }}>
                Source channel
              </label>
              <div className="flex gap-2">
                {([
                  { id: 'linkedin', label: 'LinkedIn (recommended)' },
                  { id: 'naukri',   label: 'Naukri (Resdex needed)' },
                  { id: 'both',     label: 'Both' },
                ] as const).map(ch => (
                  <button key={ch.id} type="button" onClick={() => setChannel(ch.id)}
                    className="px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors"
                    style={{
                      background: channel === ch.id ? 'var(--brand-purple, #6C47FF)' : 'var(--vscode-bg-secondary)',
                      color:      channel === ch.id ? '#fff' : 'var(--vscode-text-secondary)',
                      border:     '1px solid',
                      borderColor: channel === ch.id ? 'transparent' : 'var(--vscode-border-primary)',
                    }}>
                    {ch.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Max results */}
            <div>
              <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                Max results: {maxRes}
              </label>
              <input type="range" min={5} max={50} step={5} value={maxRes}
                onChange={e => setMaxRes(Number(e.target.value))}
                className="w-full accent-purple-600" />
            </div>

            {err && (
              <p className="text-[12px] rounded-lg px-3 py-2"
                 style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c', border: '1px solid rgba(239,68,68,0.2)' }}>
                {err}
              </p>
            )}

            {result && (
              <div className="rounded-xl p-3 text-[13px] space-y-2"
                   style={{ background: result.found > 0 ? 'rgba(16,185,129,0.08)' : 'rgba(245,158,11,0.08)', border: `1px solid ${result.found > 0 ? 'rgba(16,185,129,0.25)' : 'rgba(245,158,11,0.25)'}`, color: result.found > 0 ? '#065f46' : '#92400e' }}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>Found <strong>{result.found}</strong> leads — <strong>{result.inserted} new</strong> added, {result.skipped} duplicates skipped.</span>
                </div>
                {result.diagnostics && Object.keys(result.diagnostics).length > 0 && (
                  <div className="ml-6 space-y-1">
                    {Object.entries(result.diagnostics).map(([src, msg]) => (
                      <div key={src} className="flex items-start gap-1.5 text-[11px]">
                        <span className="font-semibold uppercase opacity-70 w-16 shrink-0">{src}:</span>
                        <span className="opacity-80">{msg}</span>
                      </div>
                    ))}
                  </div>
                )}
                {result.inserted === 0 && result.found === 0 && (
                  <p className="text-[11px] opacity-80 ml-6">
                    No results. Check that credentials in Settings are fresh, or try broader keywords.
                  </p>
                )}
                {result.inserted > 0 && <p className="text-[11px] opacity-70 ml-6">Refreshing leads table…</p>}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl text-[13px] font-medium transition-colors"
                style={{ border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={running || !keywords.trim()}
                className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: 'var(--brand-purple, #6C47FF)', color: '#fff' }}
              >
                {running ? (
                  <><LoadingSpinner size="xs" showText={false} /> Searching…</>
                ) : (
                  <><Search className="w-4 h-4" /> Find leads</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ChurnWatchlistSection
// ─────────────────────────────────────────────────────────────────────────

const RPC_FETCH_TIMEOUT_MS = 20_000;

interface ChurnWatchlistSectionProps {
  isAdmin:        boolean;
  sessionUserId:  string | null;
  /** True when the Onboarding tab is visible — triggers initial fetch once. */
  isActive:       boolean;
}

const churnCols: Column<ChurnRow>[] = [
  { key: 'name',       header: 'Name',       minWidth: 160 },
  { key: 'email',      header: 'Email',       minWidth: 220 },
  { key: 'created_at', header: 'Signed up',   minWidth: 150,
    cell: (v) => fmtDate(v as string) },
  { key: 'days_idle',  header: 'Days idle',   minWidth: 110,
    cell: (v) => <span className="font-mono font-semibold">{String(v)}</span> },
];

function ChurnWatchlistSection({ isAdmin, sessionUserId, isActive }: ChurnWatchlistSectionProps) {
  const [rows,    setRows]    = useState<ChurnRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [churnSel, setChurnSel] = useState<Set<string>>(new Set());
  const [sendModal, setSendModal] = useState<OutreachSendModalState>(CLOSED_SEND_MODAL);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const mountedRef = useRef(true);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadChurn = useCallback(async (opts?: { silent?: boolean }) => {
    if (!sessionUserId || !isAdmin) {
      setLoading(false);
      return;
    }
    if (!opts?.silent && rowsRef.current.length === 0) setLoading(true);
    setError(null);

    const timeout = new Promise<never>((_, reject) => {
      window.setTimeout(
        () => reject(new Error('Request timed out. Click Refresh to try again.')),
        RPC_FETCH_TIMEOUT_MS,
      );
    });

    try {
      const result = await Promise.race([
        supabase.rpc('admin_outreach_needed_users', { p_limit: 50 }),
        timeout,
      ]);
      if (!mountedRef.current) return;

      const { data, error: err } = result;
      if (err) {
        if ((err as { code?: string }).code === 'PGRST202') {
          console.warn('[ControlPanel] admin_outreach_needed_users not deployed yet — showing empty watchlist.');
          setRows([]);
        } else {
          setError(err.message);
        }
      } else {
        setRows((data ?? []) as ChurnRow[]);
      }
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e.message : 'Could not load onboarding watchlist.');
      setRows([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [sessionUserId, isAdmin]);

  useEffect(() => {
    if (!sessionUserId || !isAdmin) return;
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    void loadChurn({ silent: true });
  }, [sessionUserId, isAdmin, loadChurn]);

  const exportChurnCsv = (subset: ChurnRow[]) => {
    const headers = ['Name', 'Email', 'Signed up', 'Days idle'];
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csv = [headers.join(','),
      ...subset.map(r => [r.name, r.email, fmtDate(r.created_at), String(r.days_idle)].map(v => esc(String(v))).join(','))
    ].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'churn-watchlist.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Churn cols with enriched display
  const churnColsWithSel: Column<ChurnRow>[] = [
    { key: 'name', header: 'Name', minWidth: 200, alwaysVisible: true,
      cell: (_v, row) => {
        const initials = row.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
        const colors = ['#6C47FF','#0EA5E9','#10B981','#F59E0B','#EF4444','#8B5CF6'];
        const bg = colors[row.name.charCodeAt(0) % colors.length];
        return (
          <span className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            <input type="checkbox" checked={churnSel.has(row.user_id)}
              onChange={() => setChurnSel(prev => { const n = new Set(prev); n.has(row.user_id) ? n.delete(row.user_id) : n.add(row.user_id); return n; })}
              onClick={e => e.stopPropagation()}
              className="shrink-0 w-3.5 h-3.5 rounded accent-purple-600 cursor-pointer" />
            <span className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
              style={{ background: bg }}>{initials}</span>
            <span className="ds-cp-text-strong truncate font-medium">{row.name}</span>
          </span>
        );
      },
    },
    { key: 'email', header: 'Email', minWidth: 220,
      cell: (v) => v ? (
        <a href={`mailto:${v}`} className="ds-cp-email-link truncate">{String(v)}</a>
      ) : <span className="ds-cp-text-muted text-[11px] italic">No email</span>,
    },
    { key: 'created_at', header: 'Signed up', minWidth: 150,
      cell: (v) => (
        <span className="ds-cp-cell-date text-[12px]">{fmtDate(v as string)}</span>
      ),
    },
    { key: 'days_idle', header: 'Days idle', minWidth: 120,
      cell: (v) => <IdleDaysPill days={Number(v)} />,
    },
    { key: 'last_outreached_at', header: 'Last outreached', minWidth: 140,
      cell: (v) => (
        <span className="text-[12px] tabular-nums whitespace-nowrap ds-cp-text-body">
          {formatRelativeSent(v as string | null)}
        </span>
      ),
    },
  ];

  const exportTarget = churnSel.size > 0 ? rows.filter(r => churnSel.has(r.user_id)) : rows;

  const sendOnboardingOutreach = async () => {
    const targets = rows.filter(r => churnSel.has(r.user_id) && r.email?.trim());
    if (targets.length === 0) {
      setActionMsg('Select users with an email address.');
      return;
    }
    const batch = targets.slice(0, 50).map(r => r.user_id);
    setActionMsg(null);
    const result = await runOutreachSendWithModal(
      batch.length,
      setSendModal,
      () => sendOutreachIntroToUsers(batch),
      { confirm: `Send GoGrabJob onboarding nudge to ${batch.length} user(s)?` },
    );
    if (result?.ok) void loadChurn();
  };

  const sending = sendModal.open && sendModal.phase === 'sending';

  return (
    <div className="ds-cp-tab-panel flex flex-col gap-0">
      {actionMsg && (
        <p className="text-[12px] mb-2 px-3 py-2 rounded-lg"
           style={{ background: 'var(--vscode-bg-secondary)', color: 'var(--vscode-text-secondary)' }}>
          {actionMsg}
        </p>
      )}
      <div className="ds-cp-toolbar flex flex-wrap items-center gap-0">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 cursor-pointer select-none pl-1">
            <input type="checkbox"
              checked={churnSel.size === rows.length && rows.length > 0}
              ref={el => { if (el) el.indeterminate = churnSel.size > 0 && churnSel.size < rows.length; }}
              onChange={() => churnSel.size === rows.length
                ? setChurnSel(new Set())
                : setChurnSel(new Set(rows.map(r => r.user_id)))}
              className="w-3.5 h-3.5 rounded accent-purple-600"
            />
          </label>
          <span className="ds-cp-toolbar-count">{rows.length} results</span>
          {rows.length > 0 && (
            <span className="ds-cp-meta-chip ds-cp-meta-chip--warn">
              {rows.length} pending
            </span>
          )}
          <span className="ds-cp-toolbar-sep" aria-hidden />
          <button type="button" className="exa-toolbar-btn"><Filter className="w-3.5 h-3.5" /> Filter</button>
          <button type="button" className="exa-toolbar-btn"><ArrowUpDown className="w-3.5 h-3.5" /> Sort</button>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => exportChurnCsv(exportTarget)} disabled={rows.length === 0}
            className="exa-toolbar-btn" title="Export CSV">
            <Download className="w-3.5 h-3.5" />
          </button>
          {churnSel.size > 0 && (
            <button
              type="button"
              disabled={sending}
              onClick={() => void sendOnboardingOutreach()}
              className="exa-toolbar-btn exa-toolbar-btn--success"
              title="Send GoGrabJob intro email (max 50)"
            >
              <Mail className="w-3.5 h-3.5" />
              Send email ({churnSel.size})
            </button>
          )}
          {churnSel.size > 0 && (
            <span className="ds-cp-meta-chip ds-cp-meta-chip--brand">
              {churnSel.size} selected
            </span>
          )}
          <button type="button" onClick={() => void loadChurn({ silent: rows.length > 0 })} disabled={loading}
            className="exa-toolbar-btn" title="Refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && <p className="ds-cp-error text-[12.5px] mt-2 rounded-lg px-3 py-2">{error}</p>}

      <DataTable<ChurnRow>
        columns={churnColsWithSel}
        rows={rows}
        loading={false}
        pageSizeOptions={[10, 25, 50]}
        initialPageSize={10}
        reserveFloatingSpace
        ariaLabel="Incomplete onboarding users"
        emptyMessage="All users have completed onboarding."
      />
      <OutreachSendEmailModal
        state={sendModal}
        onClose={() => setSendModal(CLOSED_SEND_MODAL)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────
// TestimonialsAdminSection — TASK 10-2
// ─────────────────────────────────────────────────────────────────────────

interface Testimonial extends Record<string, unknown> {
  id:                string;
  user_id:           string;
  quote:             string;
  user_display_name: string;
  role_landed:       string;
  company_name:      string;
  approved:          boolean;
  created_at:        string;
}

interface TestimonialsAdminSectionProps {
  isAdmin:    boolean;
  hasSession: boolean;
}

function TestimonialsAdminSection({ isAdmin, hasSession }: TestimonialsAdminSectionProps) {
  const [rows,    setRows]    = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [filter,  setFilter]  = useState<'pending' | 'all' | 'approved'>('pending');
  const [saving,  setSaving]  = useState<string | null>(null);
  const [testSel, setTestSel] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!isAdmin || !hasSession) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('testimonials')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setRows((data ?? []) as Testimonial[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load testimonials.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, hasSession]);

  useEffect(() => { void load(); }, [load]);

  const handleApprove = useCallback(async (id: string) => {
    setSaving(id);
    try {
      const { error: err } = await supabase
        .from('testimonials')
        .update({ approved: true })
        .eq('id', id);
      if (err) throw err;
      setRows(prev => prev.map(r => r.id === id ? { ...r, approved: true } : r));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed.');
    } finally {
      setSaving(null);
    }
  }, []);

  const handleReject = useCallback(async (id: string) => {
    if (!window.confirm('Delete this testimonial permanently?')) return;
    setSaving(id);
    try {
      const { error: err } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', id);
      if (err) throw err;
      setRows(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setSaving(null);
    }
  }, []);

  const visible = rows.filter(r => {
    if (filter === 'pending')  return !r.approved;
    if (filter === 'approved') return r.approved;
    return true;
  });

  const pendingCount = rows.filter(r => !r.approved).length;

  const bulkApprove = async () => {
    if (!testSel.size) return;
    setSaving('bulk');
    for (const id of testSel) await supabase.from('testimonials').update({ approved: true }).eq('id', id);
    setRows(prev => prev.map(r => testSel.has(r.id) ? { ...r, approved: true } : r));
    setTestSel(new Set());
    setSaving(null);
  };

  const bulkDelete = async () => {
    if (!testSel.size) return;
    if (!window.confirm(`Delete ${testSel.size} testimonial(s) permanently?`)) return;
    setSaving('bulk');
    await supabase.from('testimonials').delete().in('id', [...testSel]);
    setRows(prev => prev.filter(r => !testSel.has(r.id)));
    setTestSel(new Set());
    setSaving(null);
  };

  return (
    <SectionCard
      title="Testimonials"
      subtitle={`${rows.length} total · ${pendingCount} pending approval`}
      icon={<MessageSquare className="w-4 h-4" />}
      badge={pendingCount > 0 ? `${pendingCount} pending` : undefined}
      badgeVariant="warning"
    >
      {/* ── Filter pills row ── */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        {(['pending', 'approved', 'all'] as const).map(f => (
          <button key={f} type="button" onClick={() => { setFilter(f); setTestSel(new Set()); }}
            className={`tbtn ${filter === f ? 'tbtn-purple' : ''}`}>
            {f === 'pending' ? `Pending (${pendingCount})` : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <button type="button" onClick={() => void bulkApprove()}
            disabled={testSel.size === 0 || saving === 'bulk' || visible.filter(t => testSel.has(t.id) && !t.approved).length === 0}
            className="tbtn tbtn-success">
            <CheckCircle2 className="w-3 h-3" />
            <span>Approve{testSel.size > 0 ? ` (${visible.filter(t => testSel.has(t.id) && !t.approved).length})` : ''}</span>
          </button>
          <button type="button" onClick={() => void bulkDelete()}
            disabled={testSel.size === 0 || saving === 'bulk'}
            className="tbtn tbtn-danger">
            <Trash2 className="w-3 h-3" />
            <span>Delete{testSel.size > 0 ? ` (${testSel.size})` : ''}</span>
          </button>
          <button type="button" onClick={() => void load()} disabled={loading} className="tbtn">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-[12px] text-red-500 mb-3">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />{error}
        </div>
      )}

      {/* Testimonials DataTable */}
      <DataTable<Testimonial>
        columns={[
          { key: 'user_display_name', header: 'Name', minWidth: 160, alwaysVisible: true,
            cell: (_v, row) => {
              const initials = row.user_display_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
              const colors = ['#6C47FF','#0EA5E9','#10B981','#F59E0B','#EF4444','#8B5CF6'];
              const bg = colors[row.user_display_name.charCodeAt(0) % colors.length];
              return (
                <span className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={testSel.has(row.id)}
                    onChange={() => setTestSel(prev => { const n = new Set(prev); n.has(row.id) ? n.delete(row.id) : n.add(row.id); return n; })}
                    onClick={e => e.stopPropagation()}
                    className="shrink-0 w-3.5 h-3.5 rounded accent-purple-600 cursor-pointer" />
                  <span className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ background: bg }}>{initials}</span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium" style={{ color: 'var(--vscode-text-primary)' }}>
                      {row.user_display_name}
                    </span>
                  </span>
                </span>
              );
            },
          },
          { key: 'quote', header: 'Testimonial', minWidth: 300,
            cell: (v) => (
              <span className="text-[12px] line-clamp-2" style={{ color: 'var(--vscode-text-secondary)' }}>
                &ldquo;{String(v).slice(0, 150)}{String(v).length > 150 ? '…' : ''}&rdquo;
              </span>
            ),
          },
          { key: 'role_landed', header: 'Role', minWidth: 140,
            cell: (_v, row) => (
              <span className="min-w-0">
                <span className="block truncate text-[12px] font-medium" style={{ color: 'var(--vscode-text-primary)' }}>
                  {row.role_landed}
                </span>
                <span className="block truncate text-[11px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
                  @ {row.company_name}
                </span>
              </span>
            ),
          },
          { key: 'approved', header: 'Status', minWidth: 100,
            cell: (v) => v ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                style={{ background: 'rgba(16,185,129,0.14)', color: '#065f46' }}>
                <Star className="w-3 h-3 fill-emerald-600" />Live
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                style={{ background: 'rgba(245,158,11,0.14)', color: '#b45309' }}>
                Pending
              </span>
            ),
          },
          { key: 'created_at', header: 'Submitted', minWidth: 120,
            cell: (v) => <span className="text-[12px]" style={{ color: 'var(--vscode-text-secondary)' }}>{fmtDate(v as string)}</span>,
          },
          { key: 'id', header: 'Actions', minWidth: 160, sortable: false,
            cell: (_v, row) => (
              <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                {!row.approved && (
                  <button type="button" onClick={() => void handleApprove(row.id)} disabled={!!saving}
                    className="tbtn tbtn-success">
                    <CheckCircle2 className="w-3 h-3" />
                    {saving === row.id ? '…' : 'Approve'}
                  </button>
                )}
                <button type="button" onClick={() => void handleReject(row.id)} disabled={!!saving}
                  className="tbtn tbtn-danger">
                  <Trash2 className="w-3 h-3" />
                  {saving === row.id ? '…' : row.approved ? 'Remove' : 'Reject'}
                </button>
              </div>
            ),
          },
        ]}
        rows={visible}
        loading={loading && visible.length === 0}
        pageSizeOptions={[10, 25, 50]}
        initialPageSize={10}
        ariaLabel="Testimonials table"
        emptyMessage={filter === 'pending' ? 'No pending testimonials — all caught up!' : 'No testimonials found.'}
        headerLeft={
          <label className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap">
            <input type="checkbox"
              checked={testSel.size === visible.length && visible.length > 0}
              ref={el => { if (el) el.indeterminate = testSel.size > 0 && testSel.size < visible.length; }}
              onChange={() => testSel.size === visible.length
                ? setTestSel(new Set())
                : setTestSel(new Set(visible.map(t => t.id)))}
              className="w-3.5 h-3.5 rounded accent-purple-600"
            />
            <span className="text-[12px]" style={{ color: 'var(--vscode-text-secondary)' }}>
              {testSel.size > 0 ? `${testSel.size}/${visible.length}` : 'All'}
            </span>
          </label>
        }
      />
    </SectionCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// JobsTabSection — browse / manage jobs fetched from peerlist_jobs & jobs tables
// ─────────────────────────────────────────────────────────────────────────

interface JobsTabSectionProps {
  isAdmin:        boolean;
  sessionUserId:  string | null;
  isActive:       boolean;
}

/** Derive human-readable source from a peerlist_jobs row */
function getJobSource(row: JobRow): string {
  if (row.source_channel) return row.source_channel;
  const u = (row.url ?? '').toLowerCase();
  if (u.includes('linkedin.com'))  return 'linkedin';
  if (u.includes('naukri.com'))    return 'naukri';
  if (u.includes('indeed.com'))    return 'indeed';
  if (u.includes('internshala.com')) return 'internshala';
  // external_slug from peerlist pages often start with the job slug
  if (row.external_slug) return 'peerlist';
  return 'manual';
}

function JobsTabSection({ isAdmin, sessionUserId, isActive }: JobsTabSectionProps) {
  const [rows,           setRows]           = useState<JobRow[]>([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const mountedRef = useRef(true);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const [editingId,      setEditingId]      = useState<string | null>(null);
  const [editDraft,      setEditDraft]      = useState<Partial<JobRow>>({});
  const [saving,         setSaving]         = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());
  const [showFindJobs,      setShowFindJobs]      = useState(false);
  const [showAddJob,        setShowAddJob]        = useState(false);
  const [showImportJobs,    setShowImportJobs]    = useState(false);
  const [freshnessDays,     setFreshnessDays]     = useState<7 | 14 | 30 | null>(7);
  const [showJobDuplicates, setShowJobDuplicates] = useState(false);

  const jobDuplicateGroups = useMemo(() => {
    const groups = new Map<string, JobRow[]>();
    for (const j of rows) {
      const key = `${j.title.trim().toLowerCase()}|${(j.company ?? '').trim().toLowerCase()}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(j);
    }
    return [...groups.values()].filter(g => g.length > 1);
  }, [rows]);

  const loadJobs = useCallback(async (opts?: { silent?: boolean }) => {
    if (!sessionUserId || !isAdmin) {
      setLoading(false);
      return;
    }
    if (!opts?.silent && rowsRef.current.length === 0) setLoading(true);
    setError(null);
    try {
      const { data: jobsData, error: jobsErr } = await supabase
        .from('peerlist_jobs')
        .select('*')
        .order('scraped_at', { ascending: false })
        .limit(500);
      if (!mountedRef.current) return;
      if (jobsErr) throw jobsErr;
      const enriched = ((jobsData ?? []) as JobRow[]).map(r => ({
        ...r,
        source_channel: r.source_channel ?? getJobSource(r),
      }));
      setRows(enriched);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e.message : 'Could not load jobs.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [isAdmin, sessionUserId]);

  // Apply freshness filter on top of loaded rows
  const freshRows = useMemo(() => {
    if (!freshnessDays) return rows;
    const cutoff = Date.now() - freshnessDays * 24 * 60 * 60 * 1000;
    return rows.filter(r => {
      const d = r.posted_at ?? r.last_seen_at ?? r.scraped_at;
      if (!d) return false;
      return new Date(d).getTime() >= cutoff;
    });
  }, [rows, freshnessDays]);

  useEffect(() => {
    if (!sessionUserId || !isAdmin) return;
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    void loadJobs({ silent: true });
  }, [sessionUserId, isAdmin, loadJobs]);

  const exportJobsCsv = useCallback((jobList: JobRow[]) => {
    const headers = ['Title', 'Company', 'Location', 'Remote', 'Job Type', 'Skills', 'Posted', 'Apply URL'];
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csvRows = jobList.map(j => [
      j.title, j.company ?? '', j.location ?? '',
      j.remote ? 'Yes' : 'No',
      j.job_type ?? '',
      (j.skills ?? []).join('; '),
      j.posted_at ? fmtDate(j.posted_at) : '',
      j.url ?? '',
    ].map(v => esc(String(v))).join(','));
    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `jobs-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const startEdit = (row: JobRow) => {
    setEditingId(row.id);
    setEditDraft({ title: row.title, company: row.company ?? '', location: row.location ?? '' });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    const patch: Record<string, unknown> = {};
    if (editDraft.title    !== undefined) patch.title    = editDraft.title;
    if (editDraft.company  !== undefined) patch.company  = editDraft.company;
    if (editDraft.location !== undefined) patch.location = editDraft.location;
    const { error: err } = await supabase.from('peerlist_jobs').update(patch).eq('id', editingId);
    setSaving(false);
    if (err) { setError(err.message); return; }
    setRows(prev => prev.map(r => r.id === editingId ? { ...r, ...editDraft } : r));
    setEditingId(null);
    setEditDraft({});
  };

  const deleteSelectedJobs = async () => {
    if (!selectedJobIds.size) return;
    if (!window.confirm(`Delete ${selectedJobIds.size} job(s)? This cannot be undone.`)) return;
    const ids = [...selectedJobIds];
    const { error: err } = await supabase.from('peerlist_jobs').delete().in('id', ids);
    if (err) { setError(err.message); return; }
    setSelectedJobIds(new Set());
    void loadJobs();
  };

  const toggleJobSelect = (id: string) => {
    setSelectedJobIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const jobCols: Column<JobRow>[] = [
    // ── Job + Company (combined rich card) ─────────────────────────────────
    { key: 'title', header: 'Job', minWidth: 260, alwaysVisible: true,
      cell: (_v, row) => {
        if (editingId === row.id) return (
          <div className="flex flex-col gap-1" onClick={e => e.stopPropagation()}>
            <input value={String(editDraft.title ?? '')}
              onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))}
              placeholder="Job title" className="w-full rounded px-2 py-1 text-[12px]" style={inputStyle} />
            <input value={String(editDraft.company ?? '')}
              onChange={e => setEditDraft(d => ({ ...d, company: e.target.value }))}
              placeholder="Company" className="w-full rounded px-2 py-1 text-[12px]" style={inputStyle} />
          </div>
        );
        const logoLetter = (row.company ?? 'J')[0].toUpperCase();
        const colors = ['#6C47FF','#0EA5E9','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899'];
        const bg = colors[(row.company ?? '').charCodeAt(0) % colors.length];
        const logoSrc = row.company_logo_url || (row.company ? googleFaviconUrl(row.company) : null);
        return (
          <span className="flex items-center gap-2.5" onClick={e => e.stopPropagation()}>
            <input type="checkbox" checked={selectedJobIds.has(row.id)}
              onChange={() => toggleJobSelect(row.id)} onClick={e => e.stopPropagation()}
              className="w-3 h-3 accent-purple-600 shrink-0" />
            {logoSrc
              ? <img src={logoSrc} alt="" className="ds-cp-company-logo shrink-0 w-8 h-8 rounded-lg object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden'); }}
                />
              : null
            }
            <span className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-[12px] font-bold text-white ${logoSrc ? 'hidden' : ''}`}
              style={{ background: bg }}>{logoLetter}</span>
            <span className="min-w-0">
              <span className="block truncate text-[12.5px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
                {row.title}
              </span>
              <span className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <span className="text-[11px]" style={{ color: 'var(--vscode-text-secondary)' }}>{row.company}</span>
                {row.location && (
                  <span className="text-[10px]" style={{ color: 'var(--vscode-text-tertiary)' }}>· {row.location}</span>
                )}
                {row.remote && (
                  <span className="ds-cp-meta-chip ds-cp-meta-chip--success text-[9px]">Remote</span>
                )}
              </span>
            </span>
          </span>
        );
      },
    },
    // ── Skills ──────────────────────────────────────────────────────────────
    { key: 'skills', header: 'Skills', minWidth: 170,
      cell: (v) => {
        const arr = (v as string[] | null) ?? [];
        if (!arr.length) return <span style={{ color: 'var(--vscode-text-tertiary)' }}>—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {arr.slice(0,3).map(s => (
              <span key={s} className="ds-cp-skill-chip">{s}</span>
            ))}
            {arr.length > 3 && (
              <span className="text-[10px]" style={{ color: 'var(--vscode-text-tertiary)' }}
                title={arr.slice(3).join(', ')}>+{arr.length - 3}</span>
            )}
          </div>
        );
      },
    },
    // ── Experience ──────────────────────────────────────────────────────────
    { key: 'experience_min_years', header: 'Exp', minWidth: 80,
      cell: (_v, row) => {
        const mn = row.experience_min_years, mx = row.experience_max_years;
        if (mn != null && mx != null) return (
          <span className="ds-cp-meta-chip ds-cp-meta-chip--info">{mn}–{mx}y</span>
        );
        if (mn != null) return (
          <span className="ds-cp-meta-chip ds-cp-meta-chip--info">{mn}+y</span>
        );
        if (row.experience_level) return (
          <span className="text-[11px] capitalize" style={{ color: 'var(--vscode-text-secondary)' }}>{row.experience_level}</span>
        );
        return <span style={{ color: 'var(--vscode-text-tertiary)' }}>—</span>;
      },
    },
    // ── Salary ──────────────────────────────────────────────────────────────
    { key: 'salary_min', header: 'Salary', minWidth: 110,
      cell: (_v, row) => {
        const mn = row.salary_min, mx = row.salary_max;
        const fmt = (n: number) => n >= 100000 ? `₹${(n/100000).toFixed(0)}L` : `₹${(n/1000).toFixed(0)}K`;
        if (mn && mx) return <span className="ds-cp-meta-chip ds-cp-meta-chip--success text-[12px]">{fmt(mn)}–{fmt(mx)}</span>;
        if (mn)       return <span className="ds-cp-meta-chip ds-cp-meta-chip--success text-[12px]">{fmt(mn)}+</span>;
        return <span style={{ color: 'var(--vscode-text-tertiary)' }}>—</span>;
      },
    },
    // ── Type ────────────────────────────────────────────────────────────────
    { key: 'job_type', header: 'Type', minWidth: 90,
      cell: (v) => v
        ? <span className="ds-cp-meta-chip ds-cp-meta-chip--brand capitalize">
            {String(v).replace(/_|-/g, ' ')}
          </span>
        : <span style={{ color: 'var(--vscode-text-tertiary)' }}>—</span>,
    },
    // ── Source ──────────────────────────────────────────────────────────────
    { key: 'source_channel', header: 'Source', minWidth: 100,
      cell: (_v, row) => <ChannelBadge channel={getJobSource(row)} />,
    },
    // ── Enrichment ──────────────────────────────────────────────────────────
    { key: 'is_active', header: 'Enrichment', minWidth: 100, sortable: false,
      cell: (_v, row) => {
        const hasSkills = (row.skills ?? []).length > 0;
        const hasSalary = row.salary_min != null || row.salary_max != null;
        const hasDesc   = !!row.description_md;
        const count     = [hasSkills, hasSalary, hasDesc, !!row.location, !!row.job_type].filter(Boolean).length;
        const tone = count >= 4 ? 'success' : count >= 2 ? 'warn' : 'danger';
        return (
          <span className={`ds-cp-meta-chip ds-cp-meta-chip--${tone} tabular-nums`}>
            {count}/5
          </span>
        );
      },
    },
    // ── Posted ──────────────────────────────────────────────────────────────
    { key: 'posted_at', header: 'Posted', minWidth: 90,
      cell: (v) => fmtDate(v as string | null),
    },
    // ── Apply + Edit (combined) ─────────────────────────────────────────────
    { key: 'url', header: 'Actions', minWidth: 110, sortable: false,
      cell: (_v, row) => {
        if (editingId === row.id) return (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <input value={String(editDraft.location ?? '')}
              onChange={e => setEditDraft(d => ({ ...d, location: e.target.value }))}
              placeholder="Location" className="w-full rounded px-2 py-1 text-[12px]" style={inputStyle} />
            <button type="button" onClick={() => void saveEdit()} disabled={saving}
              className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold"
              style={{ background: 'rgba(16,185,129,0.12)', color: '#059669' }}>
              <Save className="w-3 h-3" />{saving ? '…' : 'Save'}
            </button>
            <button type="button" onClick={() => { setEditingId(null); setEditDraft({}); }}
              className="shrink-0 p-1 rounded" style={{ color: 'var(--vscode-text-tertiary)' }}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
        return (
          <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
            {row.url && (
              <a href={String(row.url)} target="_blank" rel="noopener noreferrer"
                className="ds-cp-apply-btn inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold">
                <ExternalLink className="w-3 h-3" />Apply
              </a>
            )}
            <button type="button" onClick={() => startEdit(row)}
              className="ds-cp-icon-btn p-1.5 rounded-lg" title="Edit">
              <Edit3 className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
    <div className="ds-cp-tab-panel ds-control-panel-jobs flex flex-col gap-0">
      {/* Exa-style toolbar for Jobs */}
      <div className="ds-cp-toolbar flex flex-wrap items-center gap-0">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 cursor-pointer select-none pl-1">
            <input type="checkbox"
              checked={selectedJobIds.size === freshRows.length && freshRows.length > 0}
              ref={el => { if (el) el.indeterminate = selectedJobIds.size > 0 && selectedJobIds.size < freshRows.length; }}
              onChange={() => selectedJobIds.size === freshRows.length
                ? setSelectedJobIds(new Set())
                : setSelectedJobIds(new Set(freshRows.map(r => r.id)))}
              className="w-3.5 h-3.5 rounded accent-purple-600"
            />
          </label>
          <span className="text-[13px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
            {freshRows.length} results
          </span>
          <span className="w-px h-4" style={{ background: 'var(--vscode-border-secondary)' }} />
          <button type="button" className="exa-toolbar-btn"><Filter className="w-3.5 h-3.5" /> Filter</button>
          <button type="button" className="exa-toolbar-btn"><ArrowUpDown className="w-3.5 h-3.5" /> Sort</button>
          <span className="w-px h-4" style={{ background: 'var(--vscode-border-secondary)' }} />
          {([7, 14, 30, null] as const).map(d => (
            <button key={String(d)} type="button" onClick={() => setFreshnessDays(d)}
              className={`exa-toolbar-btn${freshnessDays === d ? ' exa-toolbar-btn--active' : ''}`}>
              {d === null ? 'All' : `${d}d`}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setShowFindJobs(true)} className="exa-toolbar-btn exa-toolbar-btn-accent">
            <Search className="w-3.5 h-3.5" /> Find Jobs
          </button>
          <button type="button" onClick={() => setShowAddJob(true)} className="exa-toolbar-btn">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
          <span className="w-px h-4" style={{ background: 'var(--vscode-border-secondary)' }} />
          <button type="button" onClick={() => setShowImportJobs(true)} className="exa-toolbar-btn" title="Import CSV">
            <Upload className="w-3.5 h-3.5" />
          </button>
          <button type="button"
            onClick={() => exportJobsCsv(selectedJobIds.size > 0 ? rows.filter(r => selectedJobIds.has(r.id)) : rows)}
            disabled={rows.length === 0}
            className="exa-toolbar-btn" title="Export CSV">
            <Download className="w-3.5 h-3.5" />
          </button>
          {selectedJobIds.size > 0 && (
            <>
              <span className="w-px h-4" style={{ background: 'var(--vscode-border-secondary)' }} />
              {editingId ? (
                <>
                  <button type="button" onClick={() => void saveEdit()} disabled={saving}
                    className="exa-toolbar-btn" style={{ color: '#059669' }}>
                    <Save className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => { setEditingId(null); setEditDraft({}); }}
                    className="exa-toolbar-btn"><X className="w-3.5 h-3.5" /></button>
                </>
              ) : (
                <button type="button" disabled={selectedJobIds.size !== 1}
                  onClick={() => { const id = [...selectedJobIds][0]; const row = rows.find(r => r.id === id); if (row) startEdit(row); }}
                  className="exa-toolbar-btn" title="Edit selected">
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
              )}
              <button type="button" onClick={() => void deleteSelectedJobs()}
                className="exa-toolbar-btn" style={{ color: '#ef4444' }} title="Delete selected">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <span className="ds-cp-meta-chip ds-cp-meta-chip--brand">
                {selectedJobIds.size} selected
              </span>
            </>
          )}
          {jobDuplicateGroups.length > 0 && (
            <button type="button" onClick={() => setShowJobDuplicates(true)}
              className="exa-toolbar-btn" style={{ color: '#f59e0b' }}
              title={`${jobDuplicateGroups.reduce((s, g) => s + g.length - 1, 0)} duplicates`}>
              <AlertCircle className="w-3.5 h-3.5" />
            </button>
          )}
          <button type="button" onClick={() => void loadJobs({ silent: rows.length > 0 })} disabled={loading} className="exa-toolbar-btn" title="Refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="ds-cp-error px-3 py-2 rounded-lg text-[12.5px] mt-2">
          {error}
        </div>
      )}

      <DataTable<JobRow>
        columns={jobCols}
        rows={freshRows}
        loading={false}
        pageSizeOptions={[20, 50, 100]}
        initialPageSize={20}
        reserveFloatingSpace
        ariaLabel="Jobs table"
        emptyMessage={freshnessDays ? `No jobs posted in last ${freshnessDays} days — try 'All' or click 'Find Jobs'.` : "No jobs yet — click 'Find Jobs' to search from channels."}
        filters={[
          {
            id: 'source_channel', label: 'Source',
            options: [
              { value: 'peerlist', label: 'Peerlist' }, { value: 'linkedin', label: 'LinkedIn' },
              { value: 'naukri', label: 'Naukri' }, { value: 'indeed', label: 'Indeed' },
              { value: 'csv', label: 'CSV Import' }, { value: 'manual', label: 'Manual' },
            ],
          },
          {
            id: 'remote', label: 'Work mode',
            options: [{ value: 'true', label: 'Remote' }, { value: 'false', label: 'On-site' }],
          },
          {
            id: 'job_type', label: 'Job type',
            options: [
              { value: 'full-time', label: 'Full-time' }, { value: 'part-time', label: 'Part-time' },
              { value: 'contract', label: 'Contract' }, { value: 'internship', label: 'Internship' },
            ],
          },
        ]}
      />
    </div>

    {showFindJobs && (
      <FindJobsModal
        onClose={() => setShowFindJobs(false)}
        onJobsFound={() => { setShowFindJobs(false); void loadJobs(); }}
      />
    )}
    {showAddJob && (
      <AddJobModal
        onClose={() => setShowAddJob(false)}
        onSaved={() => { setShowAddJob(false); void loadJobs(); }}
      />
    )}
    {showImportJobs && (
      <ImportJobsCsvModal
        onClose={() => setShowImportJobs(false)}
        onImported={() => { setShowImportJobs(false); void loadJobs(); }}
      />
    )}
    {showJobDuplicates && (
      <DuplicatesModal
        title="Duplicate Jobs"
        groups={jobDuplicateGroups.map(g => g.map(j => ({
          id: j.id,
          label: j.title,
          sublabel: j.company ?? '',
          detail: j.posted_at ? `Posted ${fmtDate(j.posted_at)}` : `Seen ${fmtDate(j.last_seen_at)}`,
        })))}
        onClose={() => setShowJobDuplicates(false)}
        onDelete={async (ids) => {
          const { error: delErr } = await supabase
            .from('peerlist_jobs').delete().in('id', ids);
          if (delErr) throw new Error(delErr.message);
          setShowJobDuplicates(false);
          void loadJobs();
        }}
      />
    )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// FindJobsModal — search jobs from channels (Peerlist/LinkedIn/Naukri)
// ─────────────────────────────────────────────────────────────────────────

interface FindJobsModalProps {
  onClose:     () => void;
  onJobsFound: () => void;
}

type JobChannel = 'peerlist' | 'linkedin' | 'naukri';

const JOB_CHANNEL_INFO: Record<JobChannel, { label: string; description: string; color: string }> = {
  peerlist: {
    label: 'Peerlist',
    description: 'India\'s top dev community job board. No credentials needed — uses public API.',
    color: '#059669',
  },
  linkedin: {
    label: 'LinkedIn',
    description: 'Requires li_at session cookie for full access. Without it, returns limited public results.',
    color: '#0077B5',
  },
  naukri: {
    label: 'Naukri',
    description: 'Requires nauk_at cookie for full access. Without credentials, only limited results available.',
    color: '#e67e22',
  },
};

function FindJobsModal({ onClose, onJobsFound }: FindJobsModalProps) {
  const [channel,     setChannel]     = useState<JobChannel>('peerlist');
  const [keywords,    setKeywords]    = useState('');
  const [location,    setLocation]    = useState('');
  const [jobType,     setJobType]     = useState('');
  const [workMode,    setWorkMode]    = useState('');
  const [expMin,      setExpMin]      = useState('');
  const [expMax,      setExpMax]      = useState('');
  const [maxResults,  setMaxResults]  = useState(20);
  const [searching,   setSearching]   = useState(false);
  const [result,      setResult]      = useState<{ found: number; inserted: number; skipped: number } | null>(null);
  const [err,         setErr]         = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [onClose]);

  // Load saved credentials
  const creds = useMemo(() => {
    try { return loadExternalCreds(); } catch { return []; }
  }, []);
  const linkedinCred = creds.find(c => c.key === 'linkedin_li_at');
  const naukriCred   = creds.find(c => c.key === 'naukri_nauk_at');

  const handleSearch = async () => {
    if (!keywords.trim()) { setErr('Enter keywords to search.'); return; }
    setSearching(true); setErr(null); setResult(null);

    const body: Record<string, unknown> = {
      keywords: keywords.trim(),
      location: location.trim() || undefined,
      max_results: maxResults,
      credentials: {
        linkedin_li_at: linkedinCred?.value ?? undefined,
        naukri_nauk_at: naukriCred?.value ?? undefined,
      },
    };

    if (channel === 'linkedin') {
      body.linkedin = {
        job_type:         jobType  ? [jobType]  : undefined,
        work_mode:        workMode ? [workMode] : undefined,
        experience_level: expMin || expMax ? undefined : undefined,
        date_posted:      'past-month',
        sort_by:          'date',
      };
    } else if (channel === 'naukri') {
      body.naukri = {
        experience_min:  expMin ? Number(expMin) : undefined,
        experience_max:  expMax ? Number(expMax) : undefined,
        job_type:        jobType ? [jobType as 'permanent' | 'contract' | 'part-time'] : undefined,
        freshness:       30 as const,
        remote_only:     workMode === 'remote' ? true : undefined,
      };
    }

    try {
      const { data, error: fnErr } = await supabase.functions.invoke('search-jobs-external', { body });
      if (fnErr) throw fnErr;
      const d = data as { ok?: boolean; total?: number; inserted?: number; sources?: string[]; error?: string };
      if (!d.ok) throw new Error(d.error ?? 'Search failed');
      const found = d.total ?? 0;
      const inserted = d.inserted ?? 0;
      const skipped  = Math.max(0, found - inserted);
      setResult({ found, inserted, skipped });
      if (inserted > 0) setTimeout(onJobsFound, 1800);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Search failed. Check edge function logs.');
    } finally {
      setSearching(false);
    }
  };

  const info = JOB_CHANNEL_INFO[channel];

  return createPortal(
    <>
      <div aria-hidden className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="w-full max-w-[560px] max-h-[90vh] overflow-y-auto rounded-2xl flex flex-col"
          style={{ background: 'var(--surface-card-elevated, var(--vscode-bg-elevated))', border: '1px solid var(--vscode-border-primary)' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b shrink-0"
               style={{ borderColor: 'var(--vscode-border-secondary)' }}>
            <div>
              <h2 className="text-[15px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
                Find Jobs
              </h2>
              <p className="text-[12px] mt-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>
                Search job listings from channels and save them to your database.
              </p>
            </div>
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70">
              <X className="w-4 h-4" style={{ color: 'var(--vscode-text-secondary)' }} />
            </button>
          </div>

          <div className="p-5 space-y-4 flex-1">
            {/* Channel selector */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider mb-2"
                     style={{ color: 'var(--vscode-text-tertiary)' }}>
                Channel
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(Object.keys(JOB_CHANNEL_INFO) as JobChannel[]).map(ch => (
                  <button
                    key={ch}
                    type="button"
                    onClick={() => setChannel(ch)}
                    className="flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl text-[12px] font-medium border transition-all"
                    style={{
                      borderColor: channel === ch ? JOB_CHANNEL_INFO[ch].color : 'var(--vscode-border-primary)',
                      background:  channel === ch ? `${JOB_CHANNEL_INFO[ch].color}14` : 'var(--vscode-bg-tertiary)',
                      color:       channel === ch ? JOB_CHANNEL_INFO[ch].color : 'var(--vscode-text-secondary)',
                    }}
                  >
                    <span className="font-semibold">{JOB_CHANNEL_INFO[ch].label}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11.5px] mt-2" style={{ color: 'var(--vscode-text-tertiary)' }}>
                {info.description}
                {channel === 'linkedin' && !linkedinCred && (
                  <span className="ml-1 font-medium" style={{ color: '#ea580c' }}>
                    No LinkedIn credential saved → go to Settings &gt; API Keys to add li_at.
                  </span>
                )}
                {channel === 'naukri' && !naukriCred && (
                  <span className="ml-1 font-medium" style={{ color: '#ea580c' }}>
                    No Naukri credential saved → go to Settings &gt; API Keys to add nauk_at.
                  </span>
                )}
              </p>
            </div>

            {/* Keywords + Location */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                  Keywords / Role *
                </label>
                <input
                  value={keywords}
                  onChange={e => setKeywords(e.target.value)}
                  placeholder="e.g. Product Manager, React Developer"
                  className="w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none"
                  style={{ background: 'var(--vscode-bg-tertiary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                  Location
                </label>
                <input
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  placeholder="e.g. Bengaluru, Remote"
                  className="w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none"
                  style={{ background: 'var(--vscode-bg-tertiary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
                />
              </div>
            </div>

            {/* Channel-specific filters */}
            {channel === 'linkedin' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                    Job Type
                  </label>
                  <select value={jobType} onChange={e => setJobType(e.target.value)}
                    className="w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none"
                    style={{ background: 'var(--vscode-bg-tertiary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}>
                    <option value="">Any</option>
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                    Work Mode
                  </label>
                  <select value={workMode} onChange={e => setWorkMode(e.target.value)}
                    className="w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none"
                    style={{ background: 'var(--vscode-bg-tertiary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}>
                    <option value="">Any</option>
                    <option value="remote">Remote</option>
                    <option value="onsite">On-site</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
              </div>
            )}

            {channel === 'naukri' && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                    Exp Min (yrs)
                  </label>
                  <input type="number" min="0" max="30" value={expMin} onChange={e => setExpMin(e.target.value)}
                    placeholder="0"
                    className="w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none"
                    style={{ background: 'var(--vscode-bg-tertiary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                    Exp Max (yrs)
                  </label>
                  <input type="number" min="0" max="30" value={expMax} onChange={e => setExpMax(e.target.value)}
                    placeholder="30"
                    className="w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none"
                    style={{ background: 'var(--vscode-bg-tertiary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                    Job Type
                  </label>
                  <select value={jobType} onChange={e => setJobType(e.target.value)}
                    className="w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none"
                    style={{ background: 'var(--vscode-bg-tertiary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}>
                    <option value="">Any</option>
                    <option value="permanent">Permanent</option>
                    <option value="contract">Contract</option>
                    <option value="part-time">Part-time</option>
                  </select>
                </div>
              </div>
            )}

            {/* Max results */}
            <div className="flex items-center gap-3">
              <label className="text-[11px] font-medium whitespace-nowrap" style={{ color: 'var(--vscode-text-secondary)' }}>
                Max results
              </label>
              {[10, 20, 50].map(n => (
                <button key={n} type="button" onClick={() => setMaxResults(n)}
                  className={`tbtn ${maxResults === n ? 'tbtn-purple' : ''}`}>
                  {n}
                </button>
              ))}
            </div>

            {/* Error / Result */}
            {err && (
              <div className="px-3 py-2 rounded-lg text-[12.5px]"
                style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c', border: '1px solid rgba(239,68,68,0.2)' }}>
                {err}
              </div>
            )}
            {result && (
              <div className="px-3 py-2.5 rounded-lg text-[13px]"
                   style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', color: '#065f46' }}>
                Found <strong>{result.found}</strong> jobs —{' '}
                <strong>{result.inserted}</strong> new,{' '}
                <strong>{result.skipped}</strong> already existed.
                {result.inserted > 0 && <span className="ml-1 opacity-70">Refreshing…</span>}
                {result.found === 0 && (
                  <p className="mt-1 text-[12px] opacity-80">
                    Try broader keywords, different location, or check your channel credentials in Settings &gt; API Keys.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t shrink-0"
               style={{ borderColor: 'var(--vscode-border-secondary)' }}>
            <button type="button" onClick={onClose} className="tbtn">Cancel</button>
            <button type="button" onClick={() => void handleSearch()} disabled={searching || !keywords.trim()}
              className="tbtn tbtn-primary">
              {searching ? (
                <><LoadingSpinner size="xs" showText={false} /><span>Searching…</span></>
              ) : (
                <><Search className="w-3.5 h-3.5" /><span>Search {JOB_CHANNEL_INFO[channel].label}</span></>
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────
// AddJobModal — manually create a job listing
// ─────────────────────────────────────────────────────────────────────────

interface AddJobModalProps { onClose: () => void; onSaved: () => void; }

const EMPTY_JOB = {
  title: '', company: '', location: '', url: '',
  job_type: '', remote: false as boolean,
  skills: '', source_channel: 'manual',
};

function AddJobModal({ onClose, onSaved }: AddJobModalProps) {
  const [form,   setForm]   = useState({ ...EMPTY_JOB });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [onClose]);

  const set = (k: keyof typeof EMPTY_JOB, v: string | boolean) =>
    setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim() || !form.company.trim()) {
      setErr('Title and Company are required.'); return;
    }
    setSaving(true); setErr(null);
    const slug = `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const payload = {
      external_slug:   slug,
      url:             form.url.trim() || `https://manual-job/${slug}`,
      title:           form.title.trim(),
      company:         form.company.trim(),
      location:        form.location.trim() || null,
      remote:          form.remote,
      job_type:        form.job_type || null,
      skills:          form.skills.split(/[,;]/).map(s => s.trim()).filter(Boolean),
      source_channel:  form.source_channel,
      is_active:       true,
      scraped_at:      new Date().toISOString(),
      last_seen_at:    new Date().toISOString(),
    };
    const { error } = await supabase.from('peerlist_jobs').insert(payload);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  };

  const inputCls = "w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none";
  const inputSty = { background: 'var(--vscode-bg-tertiary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' };
  const labelCls = "block text-[11px] font-medium mb-1";
  const labelSty = { color: 'var(--vscode-text-secondary)' };

  return createPortal(
    <>
      <div aria-hidden className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-[520px] max-h-[90vh] overflow-y-auto rounded-2xl flex flex-col"
          style={{ background: 'var(--surface-card-elevated, var(--vscode-bg-elevated))', border: '1px solid var(--vscode-border-primary)' }}
          onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-5 py-4 border-b shrink-0"
               style={{ borderColor: 'var(--vscode-border-secondary)' }}>
            <div>
              <h2 className="text-[15px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>Add Job</h2>
              <p className="text-[12px] mt-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>Manually add a job listing to the database.</p>
            </div>
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70">
              <X className="w-4 h-4" style={{ color: 'var(--vscode-text-secondary)' }} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelCls} style={labelSty}>Job Title *</label>
                <input value={form.title} onChange={e => set('title', e.target.value)}
                  placeholder="e.g. Senior Product Manager" className={inputCls} style={inputSty} autoFocus />
              </div>
              <div>
                <label className={labelCls} style={labelSty}>Company *</label>
                <input value={form.company} onChange={e => set('company', e.target.value)}
                  placeholder="Company name" className={inputCls} style={inputSty} />
              </div>
              <div>
                <label className={labelCls} style={labelSty}>Location</label>
                <input value={form.location} onChange={e => set('location', e.target.value)}
                  placeholder="e.g. Bengaluru / Remote" className={inputCls} style={inputSty} />
              </div>
              <div>
                <label className={labelCls} style={labelSty}>Job Type</label>
                <select value={form.job_type} onChange={e => set('job_type', e.target.value)}
                  className={inputCls} style={inputSty}>
                  <option value="">— Select —</option>
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                </select>
              </div>
              <div>
                <label className={labelCls} style={labelSty}>Source Channel</label>
                <select value={form.source_channel} onChange={e => set('source_channel', e.target.value)}
                  className={inputCls} style={inputSty}>
                  <option value="manual">Manual</option>
                  <option value="peerlist">Peerlist</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="naukri">Naukri</option>
                  <option value="indeed">Indeed</option>
                  <option value="csv">CSV Import</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className={labelCls} style={labelSty}>Apply URL</label>
                <input value={form.url} onChange={e => set('url', e.target.value)}
                  placeholder="https://company.com/careers/..." className={inputCls} style={inputSty} />
              </div>
              <div className="col-span-2">
                <label className={labelCls} style={labelSty}>Skills (comma-separated)</label>
                <input value={form.skills} onChange={e => set('skills', e.target.value)}
                  placeholder="React, TypeScript, Node.js, SQL" className={inputCls} style={inputSty} />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={form.remote} onChange={e => set('remote', e.target.checked)}
                className="w-3.5 h-3.5 accent-purple-600" />
              <span className="text-[12px]" style={{ color: 'var(--vscode-text-secondary)' }}>Remote-friendly / Remote-only</span>
            </label>

            {err && (
              <p className="text-[12.5px] px-3 py-2 rounded-lg"
                style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c', border: '1px solid rgba(239,68,68,0.2)' }}>
                {err}
              </p>
            )}
          </div>

          <div className="flex gap-2 px-5 py-4 border-t shrink-0"
               style={{ borderColor: 'var(--vscode-border-secondary)' }}>
            <button type="button" onClick={onClose} className="tbtn flex-1 justify-center">Cancel</button>
            <button type="button" onClick={() => void handleSave()} disabled={saving}
              className="tbtn tbtn-primary flex-1 justify-center">
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Saving…' : 'Save Job'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ImportJobsCsvModal — bulk-import jobs from CSV
// ─────────────────────────────────────────────────────────────────────────

interface ImportJobsCsvModalProps { onClose: () => void; onImported: () => void; }

const JOBS_CSV_SAMPLE = `title,company,location,remote,job_type,skills,apply_url,source_channel
Senior Product Manager,Acme Corp,Bengaluru,No,full-time,"Product Strategy, SQL, Analytics",https://acme.com/jobs/1,manual
React Developer,Startup XYZ,Remote,Yes,full-time,"React, TypeScript, Node.js",https://startup.xyz/jobs/2,linkedin
ML Engineer,BigTech Ltd,Hyderabad,No,contract,"Python, PyTorch, LLMs",https://bigtech.com/jobs/3,naukri`;

const JOBS_CSV_COL_MAP: Record<string, string> = {
  title: 'title', 'job title': 'title', role: 'title',
  company: 'company', 'company name': 'company',
  location: 'location', city: 'location',
  remote: 'remote',
  job_type: 'job_type', 'job type': 'job_type', type: 'job_type',
  skills: 'skills', 'required skills': 'skills', tags: 'skills',
  apply_url: 'url', 'apply url': 'url', url: 'url', link: 'url',
  source_channel: 'source_channel', source: 'source_channel', channel: 'source_channel',
};

function ImportJobsCsvModal({ onClose, onImported }: ImportJobsCsvModalProps) {
  const [rows,      setRows]      = useState<Record<string, string>[]>([]);
  const [importing, setImporting] = useState(false);
  const [err,       setErr]       = useState<string | null>(null);
  const [done,      setDone]      = useState<{ inserted: number; skipped: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [onClose]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setErr(null); setDone(null); setRows([]);
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const text = (ev.target?.result as string).replace(/^\uFEFF/, '');
        const parsed = parseCsv(text);
        if (parsed.length < 2) { setErr('CSV needs a header row and at least one data row.'); return; }
        const headers = parsed[0].map(h => h.trim().toLowerCase());
        const mapped = parsed.slice(1).map(row => {
          const r: Record<string, string> = {};
          headers.forEach((h, i) => {
            const field = JOBS_CSV_COL_MAP[h];
            if (field) r[field] = row[i]?.trim() ?? '';
          });
          return r;
        }).filter(r => r.title && r.company);
        if (!mapped.length) { setErr('No valid rows found — CSV must have "title" and "company" columns.'); return; }
        setRows(mapped);
      } catch { setErr('Could not parse the CSV file.'); }
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleImport = async () => {
    if (!rows.length) return;
    setImporting(true); setErr(null);
    let inserted = 0, skipped = 0;
    for (const r of rows) {
      const slug = `csv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const payload = {
        external_slug:  slug,
        url:            r.url || `https://manual-job/${slug}`,
        title:          r.title,
        company:        r.company,
        location:       r.location || null,
        remote:         /^(yes|true|1|remote)$/i.test(r.remote ?? ''),
        job_type:       r.job_type || null,
        skills:         r.skills ? r.skills.split(/[;,]/).map(s => s.trim()).filter(Boolean) : [],
        source_channel: r.source_channel || 'csv',
        is_active:      true,
        scraped_at:     new Date().toISOString(),
        last_seen_at:   new Date().toISOString(),
      };
      const { error } = await supabase.from('peerlist_jobs').insert(payload);
      if (error?.code === '23505') skipped++;
      else if (error) skipped++;
      else inserted++;
    }
    setImporting(false);
    setDone({ inserted, skipped });
    if (inserted > 0) setTimeout(onImported, 1800);
  };

  const downloadSample = () => {
    const blob = new Blob([JOBS_CSV_SAMPLE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'jobs-template.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return createPortal(
    <>
      <div aria-hidden className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-[600px] max-h-[88vh] overflow-y-auto rounded-2xl flex flex-col"
          style={{ background: 'var(--surface-card-elevated, var(--vscode-bg-elevated))', border: '1px solid var(--vscode-border-primary)' }}
          onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-5 py-4 border-b shrink-0"
               style={{ borderColor: 'var(--vscode-border-secondary)' }}>
            <div>
              <h2 className="text-[15px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>Import Jobs from CSV</h2>
              <p className="text-[12px] mt-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>
                Columns: title, company, location, remote, job_type, skills, apply_url, source_channel
              </p>
            </div>
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70">
              <X className="w-4 h-4" style={{ color: 'var(--vscode-text-secondary)' }} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:opacity-80 transition-opacity"
              style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-secondary)' }}
              onClick={() => fileRef.current?.click()}>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" />
              <Upload className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--vscode-text-tertiary)' }} />
              <p className="text-[13px] font-medium" style={{ color: 'var(--vscode-text-secondary)' }}>Click to choose a CSV file</p>
              <p className="text-[11px] mt-1" style={{ color: 'var(--vscode-text-tertiary)' }}>UTF-8 encoded, first row = headers</p>
            </div>

            <button type="button" onClick={downloadSample}
              className="text-[12px] underline underline-offset-2 transition-colors"
              style={{ color: 'var(--vscode-text-tertiary)' }}>
              Download sample CSV template
            </button>

            {err && <p className="text-[12.5px] px-3 py-2 rounded-lg"
              style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c', border: '1px solid rgba(239,68,68,0.2)' }}>{err}</p>}

            {done && (
              <div className="px-3 py-2.5 rounded-lg text-[13px]"
                style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', color: '#065f46' }}>
                <strong>{done.inserted}</strong> jobs imported.
                {done.skipped > 0 && <span className="ml-1 opacity-70">{done.skipped} skipped (duplicates or errors).</span>}
                {done.inserted > 0 && <span className="ml-1 opacity-70">Closing…</span>}
              </div>
            )}

            {rows.length > 0 && !done && (
              <div>
                <p className="text-[12px] mb-2" style={{ color: 'var(--vscode-text-secondary)' }}>
                  <strong>{rows.length}</strong> rows ready to import
                </p>
                <div className="overflow-x-auto rounded-lg border text-[11px]"
                  style={{ borderColor: 'var(--vscode-border-primary)' }}>
                  <table className="w-full min-w-[400px]">
                    <thead>
                      <tr style={{ background: 'var(--vscode-bg-secondary)' }}>
                        {['Title', 'Company', 'Location', 'Type', 'Source'].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-semibold"
                            style={{ color: 'var(--vscode-text-secondary)', borderBottom: '1px solid var(--vscode-border-primary)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 5).map((r, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--vscode-border-secondary)' }}>
                          {[r.title, r.company, r.location ?? '—', r.job_type ?? '—', r.source_channel ?? 'csv'].map((v, j) => (
                            <td key={j} className="px-3 py-1.5 truncate max-w-[120px]"
                              style={{ color: 'var(--vscode-text-primary)' }}>{v}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 px-5 py-4 border-t shrink-0"
               style={{ borderColor: 'var(--vscode-border-secondary)' }}>
            <button type="button" onClick={onClose} className="tbtn flex-1 justify-center">Cancel</button>
            <button type="button" onClick={() => void handleImport()} disabled={!rows.length || importing}
              className="tbtn tbtn-primary flex-1 justify-center">
              <Upload className="w-3.5 h-3.5" />
              {importing ? `Importing ${rows.length} rows…` : `Import ${rows.length} Jobs`}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

// VaultSection — moved to Settings > API Keys
// (kept as a no-op stub so nothing breaks if referenced elsewhere)
// ─────────────────────────────────────────────────────────────────────────

function VaultSection() {
  const [showLinkedIn, setShowLinkedIn] = useState(false);
  const [showNaukri,   setShowNaukri]   = useState(false);
  const [linkedInKey,  setLinkedInKey]  = useState('');
  const [naukriKey,    setNaukriKey]    = useState('');

  return (
    <SectionCard
      title="Encrypted API keys"
      subtitle="Platform credentials for automated outreach. Stored encrypted via Supabase Vault."
      icon={<KeyRound className="w-4 h-4" />}
    >
      {/* [REVIEW] notice */}
      <div
        className="flex gap-2 rounded-xl px-3 py-2.5 mb-4 text-[12.5px]"
        style={{ background: 'rgba(59,130,246,0.09)', border: '1px solid rgba(59,130,246,0.25)', color: '#1d4ed8' }}
      >
        <Info className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
        <span>
          <strong>REVIEW</strong> Supabase Vault (<code>vault.secrets</code>) must be enabled in the
          project settings before keys are written here. The save button below is a UI stub — wire it
          to a <code>supabase.rpc('vault_upsert_secret', …)</code> call once Vault is active.
          <strong> Never store API keys in plain text.</strong>
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <VaultKeyField
          label="LinkedIn API key"
          placeholder="Paste LinkedIn API credential…"
          value={linkedInKey}
          onChange={setLinkedInKey}
          show={showLinkedIn}
          onToggleShow={() => setShowLinkedIn(v => !v)}
        />
        <VaultKeyField
          label="Naukri API key"
          placeholder="Paste Naukri API credential…"
          value={naukriKey}
          onChange={setNaukriKey}
          show={showNaukri}
          onToggleShow={() => setShowNaukri(v => !v)}
        />
      </div>

      <button
        type="button"
        disabled
        className="mt-4 px-4 py-2.5 rounded-xl text-[13px] font-semibold opacity-40 cursor-not-allowed"
        style={{ background: 'var(--brand-purple, #6C47FF)', color: '#fff' }}
        title="Enable Supabase Vault first — see [REVIEW] notice above"
      >
        Save encrypted (Vault not configured — [REVIEW])
      </button>
    </SectionCard>
  );
}

interface VaultKeyFieldProps {
  label:          string;
  placeholder:    string;
  value:          string;
  onChange:       (v: string) => void;
  show:           boolean;
  onToggleShow:   () => void;
}

function VaultKeyField({ label, placeholder, value, onChange, show, onToggleShow }: VaultKeyFieldProps) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5"
             style={{ color: 'var(--vscode-text-tertiary)' }}>
        {label}
      </label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoComplete="off"
          className="w-full rounded-lg px-3 py-2 text-[13px] pr-10 focus:outline-none"
          style={inputStyle}
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded"
          aria-label={show ? 'Hide key' : 'Show key'}
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Small reusable sub-components
// ─────────────────────────────────────────────────────────────────────────

function SectionCard({
  title,
  subtitle,
  icon,
  badge,
  badgeVariant = 'default',
  actions,
  children,
}: {
  title:         string;
  subtitle:      string;
  icon:          React.ReactNode;
  badge?:        string;
  badgeVariant?: 'default' | 'warning' | 'error';
  actions?:      React.ReactNode;
  children:      React.ReactNode;
}) {
  const badgeColors: Record<string, { bg: string; color: string }> = {
    default: { bg: 'rgba(108,71,255,0.10)', color: 'var(--brand-purple, #6C47FF)' },
    warning: { bg: 'rgba(245,158,11,0.14)', color: '#b45309' },
    error:   { bg: 'rgba(239,68,68,0.12)',  color: '#b91c1c' },
  };
  const bc = badgeColors[badgeVariant];

  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'var(--surface-card, var(--vscode-bg-elevated))', border: '1px solid var(--vscode-border-primary)' }}
    >
      <div className="flex flex-wrap items-start gap-2 mb-4">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span style={{ color: 'var(--brand-purple, #6C47FF)' }} aria-hidden>{icon}</span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold leading-tight" style={{ color: 'var(--vscode-text-primary)' }}>
              {title}
            </h2>
            <p className="text-[12px] mt-0.5" style={{ color: 'var(--vscode-text-tertiary)' }}>
              {subtitle}
            </p>
          </div>
        </div>
        {badge && (
          <span
            className="shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: bc.bg, color: bc.color }}
          >
            {badge}
          </span>
        )}
        {actions && (
          <div className="shrink-0 flex items-center gap-2 ml-auto">
            {actions}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-[11px] font-bold uppercase tracking-wider mb-2"
          style={{ color: 'var(--vscode-text-tertiary)' }}>
        {title}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  link,
  icon,
}: {
  label:  string;
  value:  string | null | undefined;
  link?:  string | null;
  icon?:  React.ReactNode;
}) {
  if (!value && !link) return null;
  return (
    <div className="flex gap-2 text-[12.5px]">
      <span className="shrink-0 w-[110px] font-medium" style={{ color: 'var(--vscode-text-tertiary)' }}>
        {label}
      </span>
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 underline underline-offset-2 hover:opacity-80"
          style={{ color: 'var(--brand-purple, #6C47FF)' }}
        >
          {value} {icon}
        </a>
      ) : (
        <span className="flex-1 break-words" style={{ color: 'var(--vscode-text-primary)' }}>
          {value || '—'}
        </span>
      )}
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="block text-[11px] font-medium" style={{ color: 'var(--vscode-text-secondary)' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function ModalField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <label className="block text-[11px] font-medium" style={{ color: 'var(--vscode-text-secondary)' }}>
          {label}
        </label>
        {hint && (
          <span className="text-[10px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
            ({hint})
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ImportCsvModal — parse a CSV file and bulk-insert leads
// ─────────────────────────────────────────────────────────────────────────

interface ImportCsvModalProps {
  onClose:    () => void;
  onImported: () => void;
}

const CSV_COL_MAP: Record<string, keyof typeof EMPTY_IMPORT_ROW> = {
  name:            'full_name',  full_name: 'full_name',
  email:           'email',
  phone:           'phone',      mobile: 'phone', contact: 'phone',
  linkedin:        'linkedin_url', linkedin_url: 'linkedin_url',
  github:          'github_url',   github_url:  'github_url',
  source:          'source_channel', channel: 'source_channel',
  skills:          'skills',
  role:            'current_role',  title: 'current_role', designation: 'current_role',
  location:        'location',      city: 'location',
  notes:           'notes',         note: 'notes',
  status:          'status',
};

const EMPTY_IMPORT_ROW = {
  full_name: '', email: '', phone: '', linkedin_url: '', github_url: '',
  source_channel: 'direct' as SourceChannel, skills: '', current_role: '',
  location: '', notes: '', status: 'new' as LeadStatus,
};

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let cur = '', inQ = false, fields: string[] = [];
  for (let i = 0; i < text.length; i++) {
    const ch = text[i], next = text[i + 1];
    if (ch === '"') {
      if (inQ && next === '"') { cur += '"'; i++; } else { inQ = !inQ; }
    } else if (ch === ',' && !inQ) {
      fields.push(cur); cur = '';
    } else if ((ch === '\n' || ch === '\r') && !inQ) {
      fields.push(cur); cur = '';
      if (fields.some(f => f)) rows.push(fields);
      fields = [];
      if (ch === '\r' && next === '\n') i++;
    } else {
      cur += ch;
    }
  }
  if (cur || fields.length) { fields.push(cur); if (fields.some(f => f)) rows.push(fields); }
  return rows;
}

function ImportCsvModal({ onClose, onImported }: ImportCsvModalProps) {
  const [rows,       setRows]       = useState<typeof EMPTY_IMPORT_ROW[]>([]);
  const [headers,    setHeaders]    = useState<string[]>([]);
  const [importing,  setImporting]  = useState(false);
  const [err,        setErr]        = useState<string | null>(null);
  const [done,       setDone]       = useState<{ inserted: number; skipped: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [onClose]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null); setDone(null); setRows([]); setHeaders([]);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = (ev.target?.result as string).replace(/^\uFEFF/, ''); // strip BOM
        const parsed = parseCsv(text);
        if (parsed.length < 2) { setErr('CSV must have a header row and at least one data row.'); return; }
        const rawHeaders = parsed[0].map(h => h.trim().toLowerCase());
        setHeaders(rawHeaders);
        const mapped = parsed.slice(1).map(row => {
          const r = { ...EMPTY_IMPORT_ROW };
          rawHeaders.forEach((h, i) => {
            const field = CSV_COL_MAP[h];
            if (field && row[i] !== undefined) (r as Record<string, string>)[field] = row[i].trim();
          });
          // Normalise source_channel
          const validChannels = ['linkedin','naukri','facebook','google','github','discord','reddit','direct','referral','other'];
          if (!validChannels.includes(r.source_channel)) r.source_channel = 'direct' as SourceChannel;
          return r;
        }).filter(r => r.full_name);
        if (mapped.length === 0) { setErr('No valid rows found. Make sure the CSV has a "name" or "full_name" column.'); return; }
        setRows(mapped);
      } catch {
        setErr('Could not parse the CSV file.');
      }
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleImport = async () => {
    if (!rows.length) return;
    // Validate: at least some rows have email or phone
    const contactRows = rows.filter(r => r.email || r.phone);
    if (contactRows.length === 0 && rows.length > 0) {
      setErr('None of the rows have an email or phone. Please add contact info to your CSV.');
      return;
    }
    setImporting(true); setErr(null);
    let inserted = 0, skipped = 0;
    for (const r of rows) {
      const payload = {
        full_name:      r.full_name,
        email:          r.email || null,
        phone:          r.phone || null,
        linkedin_url:   r.linkedin_url || null,
        github_url:     r.github_url  || null,
        source_channel: r.source_channel,
        skills:         r.skills ? r.skills.split(/[;,]/).map(s => s.trim()).filter(Boolean) : [],
        current_role:   r.current_role || null,
        location:       r.location  || null,
        notes:          r.notes     || null,
        status:         (['new','contacted','qualified','converted','lost'].includes(r.status) ? r.status : 'new') as LeadStatus,
      };
      const { error } = await supabase.from('leads').insert(payload);
      if (error?.code === '23505') { skipped++; } // duplicate
      else if (error) { skipped++; }
      else { inserted++; }
    }
    setImporting(false);
    setDone({ inserted, skipped });
    if (inserted > 0) {
      setTimeout(() => onImported(), 1800);
    }
  };

  return createPortal(
    <>
      <div aria-hidden className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="w-full max-w-[620px] max-h-[88vh] overflow-y-auto rounded-2xl flex flex-col"
          style={{ background: 'var(--surface-card-elevated, var(--vscode-bg-elevated))', border: '1px solid var(--vscode-border-primary)' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b shrink-0"
               style={{ borderColor: 'var(--vscode-border-secondary)' }}>
            <div>
              <h2 className="text-[15px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>Import leads from CSV</h2>
              <p className="text-[12px] mt-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>
                Supported columns: name, email, phone, linkedin, github, source, skills, role, location, notes, status
              </p>
            </div>
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70">
              <X className="w-4 h-4" style={{ color: 'var(--vscode-text-secondary)' }} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {/* File picker */}
            <div
              className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors hover:opacity-80"
              style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-secondary)' }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" />
              <Plus className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--vscode-text-tertiary)' }} />
              <p className="text-[13px] font-medium" style={{ color: 'var(--vscode-text-secondary)' }}>
                Click to choose a CSV file
              </p>
              <p className="text-[11px] mt-1" style={{ color: 'var(--vscode-text-tertiary)' }}>
                UTF-8 encoded, first row must be column headers
              </p>
            </div>

            {/* Template download */}
            <button
              type="button"
              onClick={() => {
                const csv = 'name,email,phone,linkedin,source,skills,role,location,notes\nJane Doe,jane@example.com,+919876543210,https://linkedin.com/in/jane,linkedin,"React,TypeScript",Product Manager,"Bengaluru, India",Met at ProductConf';
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = 'leads-template.csv';
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              className="text-[12px] transition-colors underline underline-offset-2"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              Download CSV template
            </button>

            {err && (
              <p className="text-[12.5px] px-3 py-2 rounded-lg"
                 style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c', border: '1px solid rgba(239,68,68,0.2)' }}>
                {err}
              </p>
            )}

            {done && (
              <div className="px-3 py-2.5 rounded-lg text-[13px]"
                   style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', color: '#065f46' }}>
                <strong>{done.inserted}</strong> leads imported.
                {done.skipped > 0 && <span className="ml-1 opacity-70">{done.skipped} skipped (duplicates or errors).</span>}
                {done.inserted > 0 && <span className="ml-1 opacity-70">Closing…</span>}
              </div>
            )}

            {/* Preview table */}
            {rows.length > 0 && !done && (
              <>
                <p className="text-[12px]" style={{ color: 'var(--vscode-text-secondary)' }}>
                  <strong>{rows.length}</strong> rows ready to import.
                  {rows.filter(r => !r.email && !r.phone).length > 0 && (
                    <span className="ml-1" style={{ color: '#ea580c' }}>
                      ({rows.filter(r => !r.email && !r.phone).length} have no contact info)
                    </span>
                  )}
                </p>
                <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--vscode-border-primary)' }}>
                  <table className="w-full text-[12px] min-w-[480px]">
                    <thead>
                      <tr style={{ background: 'var(--vscode-bg-secondary)' }}>
                        {['Name', 'Email', 'Phone', 'Source', 'Role'].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-medium" style={{ color: 'var(--vscode-text-secondary)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 5).map((r, i) => (
                        <tr key={i} style={{ borderTop: '1px solid var(--vscode-border-secondary)' }}>
                          <td className="px-3 py-1.5 truncate max-w-[140px]" style={{ color: 'var(--vscode-text-primary)' }}>{r.full_name}</td>
                          <td className="px-3 py-1.5 truncate max-w-[160px]" style={{ color: r.email ? 'var(--vscode-text-primary)' : '#ea580c', fontStyle: r.email ? 'normal' : 'italic' }}>
                            {r.email || 'No email'}
                          </td>
                          <td className="px-3 py-1.5" style={{ color: 'var(--vscode-text-secondary)' }}>{r.phone || '—'}</td>
                          <td className="px-3 py-1.5" style={{ color: 'var(--vscode-text-secondary)' }}>{r.source_channel}</td>
                          <td className="px-3 py-1.5 truncate max-w-[120px]" style={{ color: 'var(--vscode-text-secondary)' }}>{r.current_role || '—'}</td>
                        </tr>
                      ))}
                      {rows.length > 5 && (
                        <tr style={{ borderTop: '1px solid var(--vscode-border-secondary)' }}>
                          <td colSpan={5} className="px-3 py-1.5 text-center" style={{ color: 'var(--vscode-text-tertiary)' }}>
                            … and {rows.length - 5} more rows
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={onClose}
                    className="flex-1 py-2.5 rounded-xl text-[13px] font-medium transition-colors"
                    style={{ border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}>
                    Cancel
                  </button>
                  <button type="button" onClick={() => void handleImport()} disabled={importing}
                    className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold transition-colors disabled:opacity-50"
                    style={{ background: 'var(--brand-purple, #6C47FF)', color: '#fff' }}>
                    {importing ? `Importing…` : `Import ${rows.length} leads`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function ForbiddenCard() {
  return (
    <div className="px-4 py-10 max-w-md mx-auto">
      <div
        className="rounded-2xl p-6 text-center"
        style={{ background: 'var(--surface-card, var(--vscode-bg-elevated))', border: '1px solid var(--vscode-border-primary)' }}
      >
        <div className="w-12 h-12 mx-auto rounded-2xl flex items-center justify-center mb-3"
             style={{ background: 'rgba(239,68,68,0.12)', color: '#b91c1c' }}>
          <ShieldAlert className="w-6 h-6" aria-hidden />
        </div>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
          Access denied
        </h2>
        <p className="text-[13px] mt-1" style={{ color: 'var(--vscode-text-secondary)' }}>
          This page is restricted to the owner-admin account.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Badge micro-components
// ─────────────────────────────────────────────────────────────────────────

function EngagementScore({ value }: { value: number }) {
  const tone = value >= 70 ? 'success' : value >= 40 ? 'warn' : 'danger';
  return <span className={`ds-cp-score ds-cp-score--${tone}`}>{value}</span>;
}

function IdleDaysPill({ days }: { days: number }) {
  const tone = days > 30 ? 'danger' : days > 14 ? 'warn' : 'info';
  return (
    <span className={`ds-cp-idle-pill ds-cp-idle-pill--${tone}`}>
      <span className="ds-cp-idle-pill__dot" aria-hidden />
      {days}d
    </span>
  );
}

function ChannelBadge({ channel }: { channel: string }) {
  const key = (channel || 'other').toLowerCase().replace(/[^a-z0-9]/g, '');
  const toneClass = [
    'linkedin', 'naukri', 'indeed', 'peerlist', 'direct', 'github', 'google',
    'referral', 'manual', 'csv', 'internshala', 'other',
  ].includes(key)
    ? `ds-cp-channel-badge--${key}`
    : 'ds-cp-channel-badge--other';
  return (
    <span className={`ds-cp-channel-badge ${toneClass}`}>
      {channel}
    </span>
  );
}

function LeadStatusBadge({ status }: { status: LeadStatus }) {
  return (
    <span className={`ds-cp-lead-badge ds-cp-lead-badge--${status}`}>
      {status}
    </span>
  );
}

const FOLLOWUP_LABELS: Record<FollowupWindow, string> = {
  overdue:   'Overdue',
  today:     'Today',
  this_week: 'This week',
  upcoming:  'Upcoming',
  none:      'Not set',
};

function FollowupBadge({ window }: { window: FollowupWindow }) {
  return (
    <span className={`ds-cp-lead-badge ds-cp-lead-badge--followup-${window}`}>
      {FOLLOWUP_LABELS[window] ?? FOLLOWUP_LABELS.none}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Shared inline styles (avoids Tailwind class conflicts)
// ─────────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background:  'var(--vscode-bg-secondary)',
  border:      '1px solid var(--vscode-border-primary)',
  color:       'var(--vscode-text-primary)',
  width:       '100%',
  borderRadius: 8,
  padding:     '6px 10px',
  fontSize:    13,
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: 'none',
  paddingRight: 28,
};
